/**
 * Zustandsmaschine und Persistenz fuer die Suchspiel-Section.
 *
 * Autonomes Custom Element (kein `is="..."`): die customized-built-in-Form
 * wird von Safari nie upgraded, es gibt in diesem Theme keinen Polyfill.
 */

/**
 * Kopieren des Rabattcodes. Wird von BEIDEN Stellen genutzt, an denen die
 * Code-Box vorkommt: der Gewinn-Karte (in <hunt-game>) und dem Gewinn-Popup
 * (GESCHWISTER von <hunt-game>, nicht Kind -- und beim Oeffnen von BasicModal
 * zusaetzlich an document.body gehaengt). Ein am Host gescopeter Handler faende
 * nur den Karten-Button und liesse den im Popup stumm; darum ein eigenes
 * Objekt, das beide Komponenten anhaengen.
 *
 * Als window-Property und nicht als top-level `const`, aus zwei Gruenden:
 *
 * 1. Es ist die Konvention dieses Themes fuer geteilte Objekte --
 *    `window.FoxTheme` (theme.js:1), `window.ProductModel`.
 * 2. Liegen zwei Suchspiel-Sections auf einer Seite, laedt der Browser diese
 *    Datei zweimal; im Theme-Editor passiert das bei jedem Section-Rerender.
 *    Ein doppelt deklariertes top-level `const` ist dann ein SyntaxError beim
 *    PARSEN des zweiten Scripts, den jedes Error-Monitoring als
 *    `Uncaught SyntaxError` meldet. Mit `window.X = window.X || {}`, dem
 *    `customElements.get`-Guard und einer `function`-Deklaration fuer
 *    defineHuntGamePopup() ist die Datei mehrfachausfuehrungs-sauber und
 *    schweigt.
 *
 * Was dieser Guard NICHT verhindert -- gemessen (Review-Runde 1), damit hier
 * keine falsche Begruendung stehen bleibt: FUNKTIONAL waere auch ein `const`
 * unauffaellig. Der erste Durchlauf ist vollstaendig fertig, bevor das zweite
 * Script ueberhaupt geparst wird; sein SyntaxError bricht nur dieses zweite
 * Script ab. Registriert ist zu dem Zeitpunkt alles. Es geht hier also um die
 * Fehlermeldung, nicht um die Funktion.
 */
window.HuntGameClipboard = window.HuntGameClipboard || {
  /**
   * Ein delegierter Klick-Listener je Wurzel. `signal` haengt ihn an den
   * AbortController der aufrufenden Komponente -- dieselbe Regel wie fuer jeden
   * anderen Listener in dieser Datei.
   */
  attach(root, signal) {
    root.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;
        // Gescopet ueber data-hunt-game-code und nicht ueber IDs: die Box
        // rendert zweimal auf derselben Seite und traegt darum keine ID.
        const button = event.target.closest('[data-hunt-game-copy]');
        // closest() laeuft ueber die Wurzel hinaus -- der Handler darf aber nur
        // die eigene Box bedienen.
        if (!button || !root.contains(button)) return;
        event.preventDefault();
        window.HuntGameClipboard.copy(button);
      },
      { signal }
    );
  },
  /**
   * Wie lange die Erfolgsmeldung stehen bleibt, in ms. Derselbe Wert wie in
   * snippets/coupon-code.liquid, damit sich beide Stellen im Shop gleich
   * verhalten.
   */
  STATUS_VISIBLE: 3500,

  /**
   * Laufende Ausblende-Timer, je Statuszeile einer.
   *
   * Als WeakMap und nicht als Eigenschaft am Element: die Box rendert zweimal
   * auf einer Seite (Karte und Pop-up), jede braucht ihren eigenen Timer, und
   * ein zweimal schnell hintereinander gedrueckter Kopieren-Button darf nicht
   * dazu fuehren, dass der ERSTE Timer die zweite Meldung wegwischt. Eine
   * WeakMap haelt die Elemente ausserdem nicht am Leben, wenn die Section im
   * Theme-Editor neu gerendert wird.
   */
  hideTimers: new WeakMap(),

  /**
   * Zeigt eine Meldung im Tooltip ueber der Pille.
   *
   * `autoHide` steuert den Unterschied, auf den es hier ankommt:
   * die Erfolgsmeldung verschwindet von selbst, der Hinweis aus Stufe 2 der
   * Kopier-Kette ("Code markiert -- bitte manuell kopieren") NICHT. Er
   * verlangt eine Handlung vom Kunden, und eine Aufforderung, die nach 3,5 s
   * verschwindet, ist keine.
   *
   * Die Reihenfolge der zwei Zeilen ist wichtig und nicht beliebig: ERST
   * sichtbar schalten, DANN den Text setzen. Die Zeile ist eine Live-Region
   * (role="status"); angesagt wird eine AENDERUNG an ihrem Inhalt. Steht das
   * Setzen des Textes vorn, faellt die Aenderung in einen Moment, in dem das
   * Element noch unsichtbar ist -- fuer den Screenreader ein Grenzfall, den
   * man nicht braucht.
   */
  showStatus(status, text, autoHide) {
    if (!status) return;
    const timers = window.HuntGameClipboard.hideTimers;
    window.clearTimeout(timers.get(status));
    timers.delete(status);

    status.setAttribute('data-visible', '');
    status.textContent = text || '';

    if (autoHide) {
      timers.set(
        status,
        window.setTimeout(() => {
          // Nur ausblenden, den Text NICHT loeschen: die Live-Region blendet
          // ueber opacity aus und bleibt damit im Accessibility-Baum. Ein
          // Leeren waere eine zweite Aenderung an ihr, ohne Nutzen.
          status.removeAttribute('data-visible');
          timers.delete(status);
        }, window.HuntGameClipboard.STATUS_VISIBLE)
      );
    }
  },

  /**
   * Dreistufig, weil `navigator.clipboard` in vielen iOS- und Android-WebViews
   * (Instagram, Facebook, TikTok) `undefined` ist -- fuer eine ueber Social
   * beworbene Landingpage ein erheblicher Trafficanteil (Design-Spec 10.4).
   *
   * `writeText` ist die ERSTE Anweisung des Handlers. Steht davor ein `await`,
   * verwirft iOS die User-Gesture und der Kopiervorgang scheitert stumm. Der
   * Code selbst kommt als Argument aus dem Button, damit auch der DOM-Zugriff
   * die Gesture nicht in eine eigene Anweisung vorziehen muss.
   *
   * Was dieses Theme heute falsch macht und hier bewusst NICHT nachgebaut wird:
   * assets/product-share.js:30 und assets/subscription-popup.js:81 rufen
   * writeText ungeprueft auf; snippets/coupon-code.liquid:129 prueft
   * isSecureContext und tut dann nichts -- der Kunde drueckt, nichts passiert,
   * keine Meldung.
   */
  copy(button) {
    let promise = null;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function' && window.isSecureContext) {
        promise = navigator.clipboard.writeText(button.dataset.code || '');
      }
    } catch (error) {
      // writeText kann in einem WebView auch synchron werfen.
      promise = null;
    }

    // Erst JETZT der DOM-Zugriff. Jede Box aktualisiert ihre EIGENE Statuszeile
    // -- ohne closest() bekaeme die Karte die Meldung des Popups oder umgekehrt.
    const box = button.closest('[data-hunt-game-code]');
    const status = box ? box.querySelector('[data-hunt-game-copy-status]') : null;

    if (!promise) {
      window.HuntGameClipboard.selectFallback(box, button, status);
      return;
    }

    promise.then(
      () => window.HuntGameClipboard.showStatus(status, button.dataset.copiedLabel, true),
      () => window.HuntGameClipboard.selectFallback(box, button, status)
    );
  },
  /**
   * Stufe 2: den sichtbaren Code markieren und den Kopierbefehl auf dieser
   * Markierung versuchen. Stufe 3 braucht keinen Code -- der Rabattcode steht
   * ohnehin als lesbarer Text in der Pille und ist nie unerreichbar.
   *
   * Warum nicht mehr input.select(): die Code-Box hatte bis zur Design-Aenderung
   * ein readonly-<input>. Die Pille ist jetzt selbst der <button>, und ein
   * Formularfeld INNERHALB eines <button> ist ungueltiges HTML. Markiert wird
   * darum der Textknoten von data-hunt-game-code-value.
   *
   * execCommand('copy') ist der Weg, den navigator.clipboard ersetzt hat, und
   * ausgerechnet in den WebViews ohne navigator.clipboard vorhanden. Gelingt er,
   * ist der Code wirklich kopiert und die Bestaetigung ist die richtige Meldung;
   * sonst bleibt der Hinweis, von Hand zu kopieren. Vorher endete diese Stufe
   * IMMER beim Hinweis, obwohl der Befehl dort meist funktioniert haette.
   *
   * Nicht am Geraet geprueft -- siehe Punkt A4 der Geraeteliste.
   */
  selectFallback(box, button, status) {
    const value = box ? box.querySelector('[data-hunt-game-code-value]') : null;
    let copied = false;

    if (value && typeof window.getSelection === 'function' && typeof document.createRange === 'function') {
      try {
        const selection = window.getSelection();
        if (selection) {
          const range = document.createRange();
          range.selectNodeContents(value);
          selection.removeAllRanges();
          selection.addRange(range);
          // Markierung stehen lassen: sie zeigt, WAS kopiert wurde, und traegt
          // Stufe 3, wenn der Befehl nicht durchgeht.
          if (typeof document.execCommand === 'function') {
            copied = document.execCommand('copy') === true;
          }
        }
      } catch (error) {
        // Ein WebView kann hier synchron werfen; dann bleibt es bei Stufe 3.
        copied = false;
      }
    }

    // autoHide nur bei Erfolg. Der Hinweis, von Hand zu kopieren, verlangt eine
    // Handlung -- eine Aufforderung, die nach 3,5 s verschwindet, ist keine.
    window.HuntGameClipboard.showStatus(
      status,
      copied ? button.dataset.copiedLabel : button.dataset.selectHint,
      copied
    );
  },
};

if (!customElements.get('hunt-game')) {
  customElements.define(
    'hunt-game',
    class HuntGame extends HTMLElement {
      /** Alles, was selbst ein Klickziel ist, bleibt Sperrflaeche. */
      static get BLOCKED_SELECTOR() {
        return 'a, button, input, select, textarea, summary, [role="button"], iframe, video';
      }

      /** Kleinere Bilder taugen nicht als Peek-Anker (Objekt waere groesser als das Bild). */
      static get MIN_IMAGE_SIZE() {
        return 120;
      }

      /**
       * Abstand, den ein Objekt zum Rand des Suchbereichs haelt -- pro Achse.
       *
       * Vertikal bleiben es 80px. Horizontal sind 80px auf einem
       * 360px-Bildschirm 44 % der Breite: es blieben 200px nutzbar, und das
       * kostet Platzierungen. Gemessen auf einer kurzen Kampagnenseite (746px
       * hoch, 360px breit, 40 Seeds je Wert):
       *
       *   Objekte   Rand 80   Rand 28
       *      3        2,77      3,00
       *      6        3,40      5,22
       *     10        3,63      5,72
       *
       * Bei der STANDARD-Anzahl 3 lagen also im Mittel nur 2,77 Objekte -- die
       * Redaktion stellt 3 ein und bekommt manchmal 2. Auf einer langen Seite
       * (2357px) und am Desktop (1385x5372) aendert der kleinere Rand nichts:
       * dort liegen mit 80 wie mit 28 immer alle Objekte. Die Aenderung wirkt
       * also genau dort, wo es klemmt, und nirgends sonst.
       *
       * 8 % der Breite je Seite, nach oben auf die alten 80px begrenzt (ab
       * 1000px Breite also unveraendert) und nach unten auf 24px. Die
       * Untergrenze ist keine Kosmetik: iOS wertet eine Wischbewegung, die
       * innerhalb der ersten rund 20px am Bildschirmrand beginnt, als
       * Zurueck-Navigation. Ein TIPP dort ist unkritisch, aber naeher als 24px
       * an die Kante soll ein Objekt trotzdem nicht.
       *
       * Warum nicht auch der vertikale Rand kleiner: dort kostet er nichts.
       * Der Suchbereich ist auf jeder brauchbaren Seite ein Vielfaches der
       * Bildschirmhoehe lang, 80px oben und unten fallen nicht ins Gewicht.
       */
      static get SAFE_MARGIN() {
        const breite = document.documentElement.clientWidth || 0;
        return { x: Math.min(80, Math.max(24, Math.round(breite * 0.08))), y: 80 };
      }

      /** Harte Untergrenze fuer die Bedienbarkeit auf Touch-Geraeten. */
      static get MIN_OBJECT_SIZE() {
        return 44;
      }

      /**
       * Spielraum der Objekt-Box bei aktivem Puls.
       *
       * MUSS mit dem HOEHEPUNKT der Keyframe `hunt-game-object-pulse` in
       * assets/hunt-game.css uebereinstimmen (`transform: scale(1.06)` bei
       * 50%). tests/hunt-game-constraints.test.js prueft beide Zahlen
       * gegeneinander und faellt, sobald eine von ihnen driftet.
       *
       * WOZU. Die Puls-Keyframe skaliert den span; der Button beschneidet mit
       * `overflow: hidden` auf das Sichtfenster. Ohne Spielraum schnitt das
       * Fenster dem skalierten Bild rund 3 % pro Seite ab -- die Kanten sahen
       * abgehackt aus (Meldung des Product Owners an einem Kampagnenbild,
       * zutreffend). Deshalb bekommt das Fenster bei aktivem Puls genau den
       * Betrag Spielraum, um den der span waechst.
       *
       * NUR AUF DEN FREIEN SEITEN. objectBox() legt den Spielraum ausdruecklich
       * nicht auf eine Seite mit Beschnitt (clipInset > 0). Dort laeuft das
       * Objekt hinter dem Bild weiter, und Spielraum legte genau diesen
       * verborgenen Teil offen: die Schnittkante lag dann sichtbar auf dem Bild
       * statt auf der Bildkante. Gemessen im Browser bei Objekt 110px und Peek
       * 40 % an einer Bildkante bei x=300: Fenster links 296,7 statt 300,0.
       * Das war die zweite Meldung zum selben Symptom, diesmal nur bei Puls.
       *
       * WARUM NICHT EINFACH DEN BUTTON SKALIEREN: der Button traegt seine
       * Position als Inline-`transform`, jede Skalierung an ihm multipliziert
       * die Verschiebung mit (gemessen: ein Objekt bei y=2925 sass unter
       * scale 1.3 bei y=3786), und er IST die Klickflaeche. Der Spielraum ist
       * stattdessen Geometrie: er geht in `--hunt-game-visible-width/-height`
       * ein und wird als Ganzes gegen die Sperrflaechen geprueft
       * (HuntGameGeometry.objectBox, Design-Spec 6.6 -- klickbar ist nur die
       * geprueffte Flaeche). Eine unvalidierte Klickflaeche war der schwerste
       * Defekt dieses Features und hat zwei Fix-Runden gekostet.
       *
       * Zwei Folgen, die dokumentiert bleiben: der Fussabdruck jedes Objekts
       * waechst um 6 %, die Platzierung wird also etwas enger; und bei einem
       * Peek-Objekt waechst das Fenster auch an der Beschnitt-Kante, wodurch
       * ein schmaler Streifen des verdeckten Teils sichtbar wird -- erst
       * dadurch ist der Puls an Peek-Objekten ueberhaupt zu sehen.
       */
      static get PULSE_HEADROOM() {
        return 1.06;
      }

      /**
       * Notausgang fuer das Messgatter (siehe canMeasure). theme.js entfernt
       * data-initializing spaetestens nach 5000ms; danach wird auch ohne
       * Interaktion gemessen.
       */
      static get LAYOUT_TIMEOUT() {
        return 5500;
      }

      /**
       * Dauer der Fund-Animation in ms. Muss mit der Angabe in
       * assets/hunt-game.css uebereinstimmen
       * (.hunt-game__object[data-found] .hunt-game__object-clip).
       *
       * GEKOPPELT an WIN_DELAY (siehe dort): die beiden Zahlen zusammen
       * entscheiden, wie viel von der Fund-Animation der Kunde vor dem Popup
       * noch sieht. Wer eine davon aendert, muss die andere mitlesen.
       * tests/hunt-game-constraints.test.js prueft beide gegen das CSS.
       */
      static get FOUND_ANIMATION() {
        return 450;
      }

      /**
       * Sicherheitsnetz fuer das DOM-Aufraeumen: Animationsdauer plus 100ms.
       * `animationend` kann ausbleiben -- iOS pausiert Animationen in
       * Hintergrund-Tabs (Design-Spec 10.7), und ohne geladenes CSS gibt es gar
       * keine Animation. Was zuerst eintritt, gewinnt.
       */
      static get FOUND_CLEANUP_TIMEOUT() {
        return HuntGame.FOUND_ANIMATION + 100;
      }

      /**
       * Pause zwischen dem letzten Fund und dem Gewinn-Zustand. Ohne sie
       * ueberlagert das Popup die Animation des Objekts, das das Spiel gerade
       * abgeschlossen hat, und der Fund geht optisch unter.
       *
       * DIE PAUSE BEGINNT MIT DEM KLICK, NICHT MIT `animationend`.
       * handleWin() wird synchron aus handleObjectClick() gerufen, der Timer
       * laeuft also ab dem Fund -- und 300ms sind KUERZER als die 450ms der
       * Fund-Animation (FOUND_ANIMATION). Der Wechsel in den Gewinn-Zustand
       * raeumt die Objekt-Ebene per clearLayer() ab, das letzte Objekt
       * verliert damit die letzten ~150ms seiner Animation.
       *
       * Das ist ABSICHT und nachgemessen, nicht ein Versehen: an der
       * 300ms-Marke steht der span der Fund-Keyframe bei Opazitaet 0,122 --
       * abgeschnitten werden also nur die letzten ~12% des Ausblendens, die
       * ohnehin niemand mehr sieht. Ein Verlaengern auf 450ms+ kauft optisch
       * nichts und laesst den Kunden nach seinem letzten Fund nur laenger
       * warten. Frueher stand hier (und in Design-Spec 7) die Behauptung, die
       * Pause setze nach `animationend` ein; das war schlicht falsch.
       */
      static get WIN_DELAY() {
        return 300;
      }

      /**
       * Debounce der Neuberechnung. Ein Ziehen am Fensterrand, ein
       * Orientierungswechsel und ein Folienwechsel im Karussell liefern alle
       * eine Serie von Ereignissen; gerechnet wird erst, wenn sie abgerissen
       * ist.
       */
      static get REFLOW_DEBOUNCE() {
        return 150;
      }

      connectedCallback() {
        // Re-Init absichern: bei einer Section-Bewegung im Theme-Editor
        // (shopify:section:load, Task 13) kann connectedCallback ein zweites
        // Mal laufen, bevor disconnectedCallback dazwischenkommt. Ohne Abort
        // haengen dann zwei Klick-Listener am Start-Button.
        if (this.abortController) this.abortController.abort();
        // Dieselbe Situation trifft die Objekt-Ebene: sie haengt an document.body
        // und wuerde ohne dazwischenliegendes disconnectedCallback als Rest der
        // alten Runde stehen bleiben.
        this.stopWaitingForLayout();
        // Dasselbe fuer die Layout-Beobachtung, und unabhaengig davon, ob noch
        // eine Ebene haengt: ein zweiter ResizeObserver auf #MainContent wuerde
        // bei jedem Umbruch eine zweite Neuberechnung anstossen.
        this.unobserveLayout();
        if (this.layer) this.clearLayer();

        this.settings = this.readSettings();
        this.designMode = this.dataset.designMode === 'true';

        this.total = this.settings.objectCount;
        this.foundCount = 0;

        this.startButton = this.querySelector('[data-hunt-game-start]');
        this.restartButton = this.querySelector('[data-hunt-game-restart]');

        this.objects = [];
        this.layer = null;
        // Merker und Timer des Gewinns. winPending macht handleWin() idempotent
        // -- es hat zwei Aufrufer (Rundenstart und letzter Fund), und der Gewinn
        // darf nur EINMAL zugeteilt und das Popup nur einmal geoeffnet werden.
        // clearTimeout zuerst: bei einem Re-Connect im Theme-Editor koennte ein
        // Gewinn-Timer des vorigen Connects noch laufen.
        window.clearTimeout(this.winTimer);
        this.winPending = false;
        this.winTimer = null;
        // Die Aufraeum-Timer der Fund-Animation, einer je gefundenem Objekt.
        // Gefuehrt, damit disconnectedCallback() sie verwerfen kann; dieselbe
        // Regel wie fuer den Gewinn-Timer und die Listener am Abort-Signal.
        this.clearCleanupTimers();
        this.cleanupTimers = new Set();
        this.layoutWait = null;
        this.resizeObserver = null;
        this.repositionTimer = null;
        this.slideWrappers = [];
        this.slideHandler = null;
        // Die Fenster-Listener haengen am AbortController-Signal und damit an
        // DIESEM Connect, observeLayout() laeuft aber pro Runde. Der Merker
        // verhindert, dass sich bei jedem Neustart ein Listener dazulegt.
        this.layoutListenersBound = false;
        this.lastDocumentWidth = document.documentElement.clientWidth;
        this.lastContentBox = null;
        this.lastViewportHeight = window.innerHeight;
        this.lastEventViewportHeight = window.innerHeight;
        this.lastViewportChangeAt = 0;

        this.abortController = new AbortController();
        const signal = this.abortController.signal;

        if (this.startButton) {
          this.startButton.addEventListener('click', () => this.startGame(), { signal });
        }
        if (this.restartButton) {
          this.restartButton.addEventListener('click', () => this.restartGame(), { signal });
        }
        // Die Code-Box der Gewinn-Karte. Das Popup haengt denselben Handler an
        // sich selbst -- es ist Geschwister dieses Elements und von hier aus
        // nicht erreichbar.
        window.HuntGameClipboard.attach(this, signal);

        this.restoreState();

        // Merker fuer den shopify:section:load-Listener: hat DIESER Connect
        // schon eine Runde gebaut? Genau dann, wenn restoreState() gestartet
        // hat -- ueber autostart oder ueber preview_objects. Begruendung und
        // Messung stehen am Listener in setupDesignMode().
        this.roundBuiltOnConnect = this.state === 'playing';

        // Zuletzt, und nur im Theme-Editor: die Section-Ereignisse der
        // Plattform und die Popup-Vorschau. restoreState() hat den
        // Objekt-Preview (preview_objects) zu diesem Zeitpunkt schon gestartet.
        this.setupDesignMode();
        if (this.designMode) this.openPopupPreview();
      }

      disconnectedCallback() {
        this.stopWaitingForLayout();
        // Sonst laeuft der Gewinn-Timer nach dem Abraeumen weiter und setzt
        // Zustand und Popup an einem Element, das nicht mehr im Dokument haengt.
        window.clearTimeout(this.winTimer);
        this.winTimer = null;
        this.clearCleanupTimers();
        this.clearLayer();
        if (this.abortController) this.abortController.abort();
      }

      /**
       * Verwirft die noch offenen Aufraeum-Timer der Fund-Animation. Beim
       * ersten Connect gibt es das Set noch nicht.
       */
      clearCleanupTimers() {
        if (!this.cleanupTimers) return;
        for (const timer of this.cleanupTimers) window.clearTimeout(timer);
        this.cleanupTimers.clear();
      }

      readSettings() {
        const d = this.dataset;
        // Math.round, nicht parseFloat allein: alle Zahl-Settings dieser Section
        // sind ganzzahlig (range mit step 1 oder 5). Ein nicht-ganzzahliger Wert
        // aus einem handeditierten JSON-Template wuerde in makeBands() die
        // Invariante "letztes Band endet exakt bei areaBottom" brechen.
        const num = (value, fallback) => {
          const parsed = Number.parseFloat(value);
          return Number.isFinite(parsed) ? Math.round(parsed) : fallback;
        };
        return {
          sectionId: d.sectionId,
          popupId: d.popupId,
          objectCount: num(d.objectCount, 3),
          sizeDesktop: num(d.sizeDesktop, 100),
          sizeMobile: num(d.sizeMobile, 70),
          sizeVariation: num(d.sizeVariation, 30),
          minDistance: num(d.minDistance, 200),
          imagePeek: d.imagePeek === 'true',
          imagePeekPercent: num(d.imagePeekPercent, 40),
          // Anteil der Objekte, die hinter einem Bild liegen sollen. Der
          // Rest wird auf freien Flaechen platziert. Zielvorgabe, keine
          // Garantie -- siehe placeObjects in hunt-game-geometry.js.
          imagePeekShare: num(d.imagePeekShare, 50),
          pulse: d.pulse === 'true',
          autostart: d.autostart === 'true',
          // Standard true: fehlt das Attribut (aeltere gespeicherte Section),
          // soll der Sprung trotzdem stattfinden. Nur ein ausdrueckliches
          // 'false' schaltet ihn ab.
          scrollToCard: d.scrollToCard !== 'false',
          // Der kundensichtbare Fallback fuer ein geleertes counter_template
          // liegt UEBERSETZT in sections/hunt-game.liquid
          // ('hunt_game.counter_template_fallback' | t) -- wie jeder andere
          // kundensichtbare Fallback dieser Section. Hier steht nur noch die
          // sprachneutrale Notform fuer den Fall, dass das Attribut ganz
          // fehlt (aus der ausgelieferten Liquid-Seite unerreichbar, sie
          // schreibt es immer): ein leerer Zaehler waere die schlechtere
          // Antwort, ein deutsches Literal im JS die falsche.
          counterTemplate: d.counterTemplate || '[[found]] / [[total]]',
          counterPosition: d.counterPosition || 'center',
          previewObjects: d.previewObjects === 'true',
          previewPopup: d.previewPopup === 'true',
        };
      }

      get searchArea() {
        return document.getElementById('MainContent');
      }

      get objectTemplate() {
        return this.querySelector('[data-hunt-game-object-template]');
      }

      /**
       * Der Zaehler. Serverseitig gerendert (siehe sections/hunt-game.liquid) --
       * hier wird er nur gelesen und beschriftet, nie erzeugt. Er fehlt, wenn
       * kein Objektbild gesetzt ist (dieselbe Bedingung wie beim Template).
       */
      get counterElement() {
        return this.querySelector('[data-hunt-game-counter]');
      }

      get isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      /**
       * "Kein Objektbild gesetzt" -- dann gibt es nichts zu platzieren, und
       * weder Start noch Neustart duerfen eine leere Runde beginnen.
       *
       * Abgeleitet aus dem Vorhandensein des Templates, NICHT aus
       * startButton.disabled: die Liquid-Seite setzt beides unter derselben
       * Bedingung (`{% unless has_image %}disabled` am Button, `{% if has_image %}`
       * um das Template), aber das disabled-Attribut wird vom state-Setter
       * mitbenutzt. Ein gelatchter Wert aus connectedCallback wuerde bei einem
       * Re-Connect im Zustand "playing" -- Section verschieben im Theme-Editor --
       * dauerhaft auf true kleben und Start UND Neustart totlegen. Das Template
       * ruehrt niemand an.
       */
      get startDisabled() {
        return !this.objectTemplate;
      }

      /**
       * Messbereitschaft. `readyState === 'complete'` allein genuegt in diesem
       * Theme NICHT: layout/theme.liquid rendert `<body data-initializing>`, und
       * theme.css gibt damit jeder dritten und spaeteren .shopify-section in
       * #MainContent `content-visibility: auto` ohne contain-intrinsic-size.
       * Solche Sections sind groessenbeschraenkt -- ihre Hoehe ist 0 und ihre
       * Nachkommen werden nicht layoutet. Gemessen wuerden also nur die obersten
       * Sections, die Baender teilten nur den Seitenanfang, und kein Bild
       * unterhalb des ersten Bildschirms waere Kandidat: derselbe
       * Ballungs-Fehler wie ein img.complete-Filter, nur ueber CSS.
       *
       * theme.js entfernt das Attribut erst bei der ersten Interaktion oder nach
       * 5000ms (delayUntilInteraction) -- lange nach `load`. Die Ausnahmen in
       * theme.css greifen nur im Design-Mode und bei .no-js; die dort ebenfalls
       * genannte Klasse .no-touch wird in diesem Theme nirgends gesetzt, der
       * Fall betrifft also auch den Desktop.
       */
      get canMeasure() {
        return (
          document.readyState === 'complete' && !document.body.hasAttribute('data-initializing')
        );
      }

      /**
       * Wartet auf Messbereitschaft und baut die Runde danach erneut. Drei
       * Signale, damit kein Pfad haengen bleibt: `load`, das Entfernen von
       * data-initializing und ein Timeout als Notausgang.
       */
      waitForLayout() {
        if (this.layoutWait) return;
        const signal = this.abortController.signal;
        const retry = () => {
          if (!this.layoutWait || signal.aborted) return;
          this.stopWaitingForLayout();
          this.buildRound();
        };
        const retryIfReady = () => {
          if (this.canMeasure) retry();
        };
        const observer = new MutationObserver(retryIfReady);
        this.layoutWait = {
          observer,
          // Notausgang: nach LAYOUT_TIMEOUT wird gemessen, was da ist. Ein
          // Spiel mit unguenstig verteilten Objekten ist besser als keins.
          timer: window.setTimeout(retry, HuntGame.LAYOUT_TIMEOUT),
        };
        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['data-initializing'],
        });
        if (document.readyState !== 'complete') {
          window.addEventListener('load', retryIfReady, { once: true, signal });
        }
        signal.addEventListener('abort', () => this.stopWaitingForLayout(), { once: true });
      }

      stopWaitingForLayout() {
        if (!this.layoutWait) return;
        this.layoutWait.observer.disconnect();
        window.clearTimeout(this.layoutWait.timer);
        this.layoutWait = null;
      }

      get state() {
        return this.dataset.state;
      }

      set state(value) {
        this.dataset.state = value;
        if (this.startButton) {
          const playing = value === 'playing';
          // startDisabled (kein Objektbild) darf nie ueberschrieben werden --
          // sonst wird ein hart deaktivierter Button bei jedem restoreState()
          // wieder aktiv, obwohl es nichts zu finden gibt.
          this.startButton.disabled = playing || this.startDisabled;
          const label = playing
            ? this.startButton.dataset.labelPlaying
            : this.startButton.dataset.labelIdle;
          if (label) this.startButton.textContent = label;
        }
        // Der Zaehler haengt am Zustand, nicht am ersten Fund. Er MUSS bereits
        // sichtbar sein, bevor buildRound() die Sperrflaechen erhebt: solange er
        // `hidden` traegt, ist sein Rechteck 0x0, und collectBlockedRects() wuerde
        // in der ERSTEN Runde ein Objekt unter ihm zulassen. Der Zustand wird in
        // startGame() vor buildRound() gesetzt, damit stimmt die Reihenfolge.
        //
        // ANGENOMMENER PREIS, bitte nicht "reparieren": buildRound() kann in
        // waitForLayout() zurueckkehren und dort bis zu LAYOUT_TIMEOUT (5500ms)
        // warten. In dieser Zeit steht der Zaehler sichtbar auf dem
        // serverseitigen Anfangstext ("0 von <object_count>"), obwohl noch kein
        // Objekt liegt -- und weil er eine Live-Region ist, kann VoiceOver einen
        // Zielwert ansagen, den buildRound() spaeter nach unten korrigiert (auf
        // die tatsaechlich platzierte Anzahl). Die Alternative waere, ihn erst
        // nach dem Platzieren einzublenden; dann fehlte er in genau dem Moment,
        // in dem seine Flaeche gesperrt werden muss, und ein Objekt koennte
        // unsichtbar hinter ihm landen. Ein unerreichbares Objekt ist der
        // schwerere Fehler (Design-Spec 12).
        const counter = this.counterElement;
        if (counter) counter.hidden = value !== 'playing';
      }

      // --- Persistenz. Jeder Zugriff gekapselt: localStorage wirft in Safaris
      // --- privatem Modus, das Spiel muss trotzdem laufen (dann ohne Gedaechtnis).
      get foundKey() {
        return `hunt-game:${this.settings.sectionId}:found`;
      }

      get wonKey() {
        return `hunt-game:${this.settings.sectionId}:won`;
      }

      readFound() {
        if (this.designMode) return 0;
        try {
          const raw = window.sessionStorage.getItem(this.foundKey);
          const parsed = Number.parseInt(raw, 10);
          return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
        } catch (error) {
          return 0;
        }
      }

      writeFound(value) {
        if (this.designMode) return;
        try {
          window.sessionStorage.setItem(this.foundKey, String(value));
        } catch (error) {
          /* Storage nicht verfuegbar: Spiel laeuft ohne Gedaechtnis weiter. */
        }
      }

      readWon() {
        if (this.designMode) return false;
        try {
          return window.localStorage.getItem(this.wonKey) === '1';
        } catch (error) {
          return false;
        }
      }

      writeWon() {
        if (this.designMode) return;
        try {
          window.localStorage.setItem(this.wonKey, '1');
        } catch (error) {
          /* siehe writeFound */
        }
      }

      restoreState() {
        if (this.readWon()) {
          this.state = 'won';
          return;
        }
        // Geklemmt auf total: Ein Merchant kann object_count verkleinern,
        // waehrend eine Kundensession unter derselben section.id noch einen
        // hoeheren found-Wert im sessionStorage stehen hat. Ungeklemmt wuerde
        // Task 11s Gewinn-Check dadurch entweder sofort ausloesen (>=) oder nie
        // mehr erreichbar sein (===).
        this.foundCount = Math.min(this.readFound(), this.total);
        // Wiring fuer Task 11: bei einer wiederaufgenommenen Runde muss der
        // Zaehler sofort den wiederhergestellten Stand zeigen, nicht erst nach
        // dem naechsten Fund. updateCounter() ist noch ein leerer Stub, hier
        // passiert also aktuell nichts, aber der Aufruf ist an der richtigen
        // Stelle verdrahtet.
        this.updateCounter();
        // Zustand IMMER zuruecksetzen, und zwar VOR dem moeglichen Start.
        //
        // `state` liegt als data-state am Element, und das Attribut ueberlebt
        // einen Re-Connect: verschiebt der Merchant die Section im
        // Theme-Editor, kommt das Element mit data-state="playing" aus seinem
        // alten Leben zurueck. startGame() haette dann seinen eigenen
        // Wiedereintritts-Schutz gegen sich (`if (this.state === 'playing')
        // return`) -- waehrend disconnectedCallback die Ebene bereits geraeumt
        // hat. Gemessen wurde genau das: nach dem Reconnect 0 Objekte, 0 Ebenen,
        // Karte auf "Suche laeuft" und der Start-Button dauerhaft deaktiviert,
        // also eine Sackgasse ohne Ausweg.
        //
        // Betrifft beide Startwege, den Objekt-Preview des Editors und
        // `autostart`.
        this.state = 'idle';
        if (this.settings.autostart || (this.designMode && this.settings.previewObjects)) {
          this.startGame();
        }
      }

      /**
       * Start einer Runde -- und BEWUSST nicht dasselbe wie restartGame().
       *
       * DIE ASYMMETRIE, die beim ersten Lesen ueberrascht: startGame() ruehrt
       * `foundCount` NICHT an, restartGame() setzt es auf 0 (und schreibt die
       * 0 in den sessionStorage).
       *
       * Grund: die beiden Methoden bedienen zwei verschiedene Absichten.
       * - startGame() ist WIEDERAUFNAHME. Es laeuft beim Reload einer
       *   angefangenen Runde, bei `autostart` und beim Klick auf den
       *   Start-Button. Der Fortschritt aus dem sessionStorage muss dabei
       *   erhalten bleiben: buildRound() platziert nur die noch fehlenden
       *   Objekte (objectCount - foundCount) und der Zaehler zeigt weiter
       *   "2 von 3" (Design-Spec 5.2).
       * - restartGame() ist eine NEUE RUNDE, ausgeloest vom Textlink auf der
       *   Gewinnkarte. Dort ist Fortschritt-Behalten sinnlos: foundCount
       *   stuende auf total, es gaebe nichts mehr zu platzieren.
       *
       * Was BEIDE gleich behandeln: der "gewonnen"-Merker im localStorage
       * bleibt in jedem Fall stehen -- wer nach dem Sieg neu startet und
       * abbricht, darf den Rabattcode nicht verlieren.
       */
      startGame() {
        // Ohne Objektbild gibt es nichts zu platzieren (Task 9 klont das Bild
        // in buildRound()). Die Liquid-Seite deaktiviert den Button dafuer
        // bereits hart (startDisabled) -- dieselbe Bedingung muss aber auch
        // Autostart und den Design-Mode-Objekt-Preview blockieren, die nicht
        // ueber einen Klick auf den Button laufen.
        if (this.startDisabled) return;
        if (this.state === 'playing') return;
        this.resetWin();
        this.state = 'playing';
        this.buildRound();
      }

      restartGame() {
        // Gleiche Bedingung wie in startGame(): ohne Objektbild gibt es nichts
        // zu platzieren. Der Neustart-Button sitzt auf der Gewinnkarte, und die
        // wird auch dann noch gezeigt, wenn die Redaktion das Objektbild nach
        // einem Gewinn entfernt (der won-Merker liegt im localStorage). Ohne
        // diesen Guard liefe eine leere Runde: Zustand "playing", kein Objekt.
        if (this.startDisabled) return;
        // Der "gewonnen"-Merker bleibt bewusst bestehen: wer neu startet und
        // abbricht, darf den Code nicht verlieren.
        //
        // HIER wird foundCount genullt, in startGame() ausdruecklich NICHT --
        // die Begruendung der Asymmetrie steht am Kommentar von startGame().
        this.foundCount = 0;
        this.writeFound(0);
        this.resetWin();
        this.state = 'playing';
        this.buildRound();
      }

      /**
       * Macht den Gewinn fuer eine neue Runde wieder ausloesbar und verwirft
       * einen noch laufenden Gewinn-Timer. Ohne das Verwerfen koennte der Timer
       * einer abgebrochenen Runde die neue Runde in den Zustand "won" reissen.
       */
      resetWin() {
        window.clearTimeout(this.winTimer);
        this.winTimer = null;
        this.winPending = false;
      }

      // --- Kandidaten-Sammlung. Alle Rechtecke in DOKUMENT-Koordinaten, weil die
      // --- Geometrie mit einer scroll-unabhaengigen Konvention rechnet.

      /** Viewport-Rect plus Scroll-Offset -> Dokument-Koordinaten. */
      documentRect(element) {
        const r = element.getBoundingClientRect();
        const dx = window.scrollX;
        const dy = window.scrollY;
        return { left: r.left + dx, top: r.top + dy, right: r.right + dx, bottom: r.bottom + dy };
      }

      /**
       * Untergrenze der Objektgroesse fuer eine PEEK-Platzierung.
       *
       * Hinter einem Bild zaehlt fuer den Kunden nur der SICHTBARE Streifen, und
       * der ist peekPercent der Objektgroesse. Ein Objekt mit 44px waere bei 40%
       * Peek nur 17,6px hoch anzutippen -- unter der Tap-Ziel-Untergrenze.
       * Deshalb waechst das Objekt so weit, dass der Streifen selbst 44px
       * erreicht (bei 40% sind das 110px).
       *
       * Damit wird `size_mobile` der Redaktion nach oben ueberschrieben. Das ist
       * dieselbe Logik, mit der MIN_OBJECT_SIZE das heute schon tut, und vom
       * Product Owner ausdruecklich so entschieden: Objekte werden auf Mobil
       * sichtbar groesser und das Spiel etwas leichter.
       *
       * GILT NUR FUER PEEK-PLATZIERUNGEN. Ein Objekt auf einer freien Flaeche
       * ist vollstaendig sichtbar und braucht nichts als die 44px selbst
       * (MIN_OBJECT_SIZE). Frueher hing diese Grenze allein an
       * `enable_image_peek` und traf damit jedes Objekt der Runde -- auch die
       * Flaechen-Anker, die gar nichts zu beschneiden haben. Mit dem
       * Peek-ANTEIL (image_peek_share) waere das offensichtlich geworden:
       * Objekte auf freier Flaeche waeren ohne jeden Gegenwert auf 110px
       * aufgeblasen. Die Auswahl trifft jetzt placeObjects pro Platzierung
       * (sizeConfig.minSize / sizeConfig.minSizePeek).
       */
      peekMinObjectSize() {
        const min = HuntGame.MIN_OBJECT_SIZE;
        if (!this.settings.imagePeek) return min;
        const percent = this.settings.imagePeekPercent;
        // percent 0 kann nur aus einem handeditierten JSON-Template kommen
        // (Schema-Minimum ist 35); ohne Guard waere das Ergebnis Infinity.
        if (!percent || percent <= 0) return min;
        return Math.max(min, Math.ceil(min / (percent / 100)));
      }

      /**
       * Untergrenze fuer Bild-Anker. Ein Anker, der kleiner als das Objekt ist,
       * laesst es laengs der Kante ueberstehen -- die Illusion "lugt hinter dem
       * Bild hervor" bricht dann.
       */
      effectiveMinImageSize() {
        return Math.max(HuntGame.MIN_IMAGE_SIZE, this.peekMinObjectSize());
      }

      /**
       * Liegt das Bild im sichtbaren Ausschnitt seines Karussells?
       * Ohne Karussell trivial wahr. Verlangt wird die vollstaendige Deckung
       * (mit 1px Subpixel-Toleranz): eine halb ueberstehende Folie ist ein
       * schlechter Anker, weil das Objekt in ihrem verdeckten Teil landen kann.
       */
      isSlideInView(img) {
        const slide = img.closest('.swiper-slide');
        if (!slide) return true;
        const viewport = slide.closest('.swiper');
        if (!viewport) return true;
        const inner = this.documentRect(img);
        const outer = this.documentRect(viewport);
        const tolerance = 1;
        return (
          inner.left >= outer.left - tolerance &&
          inner.right <= outer.right + tolerance &&
          inner.top >= outer.top - tolerance &&
          inner.bottom <= outer.bottom + tolerance
        );
      }

      collectImageCandidates() {
        const area = this.searchArea;
        if (!area) return [];
        const min = this.effectiveMinImageSize();
        const candidates = [];

        for (const img of area.querySelectorAll('img')) {
          if (this.contains(img)) continue;
          if (img.closest('.swiper-slide-duplicate')) continue;
          // Karussell-Folien: es zaehlt, ob die Folie im sichtbaren Ausschnitt
          // ihres .swiper-Containers liegt -- geprueft an den Rechtecken.
          // Klassen taugen dafuer nicht: swiper-slide-active markiert genau EINE
          // Folie, unabhaengig von slidesPerView (bei vier sichtbaren Bildern
          // fielen drei heraus), und vor der Initialisierung traegt keine Folie
          // die Klasse (dann fiele jede heraus). .swiper-slide ist in diesem
          // Theme serverseitig gerendert, der Fall ist also der Normalfall.
          if (!this.isSlideInView(img)) continue;
          if (img.closest('[aria-hidden="true"]')) continue;
          if (img.hidden) continue;

          // Bewusst KEIN img.complete / naturalWidth: bei loading="lazy" melden
          // Bilder ausserhalb des Viewports complete=false und naturalWidth=0.
          // Ein Filter darauf wuerde fast alles unterhalb des ersten Bildschirms
          // ausschliessen. Die Layout-Box steht dank der width/height-Attribute
          // von image_tag bereits vor dem Laden fest.
          const rect = this.documentRect(img);
          if (rect.right - rect.left < min || rect.bottom - rect.top < min) continue;

          candidates.push({ rect, anchor: img });
        }
        return candidates;
      }

      collectSurfaceCandidates() {
        const area = this.searchArea;
        if (!area) return [];
        const candidates = [];
        for (const section of area.querySelectorAll('.shopify-section')) {
          if (section.contains(this)) continue;
          const rect = this.documentRect(section);
          // Dieselbe Untergrenze wie fuer die Objekte selbst: eine Flaeche,
          // die niedriger als das Objekt ist, laesst es oben oder unten
          // ueberstehen. MIN_OBJECT_SIZE, NICHT die erhoehte Peek-Grenze: auf
          // einer Flaeche liegt das Objekt unbeschnitten und darf 44px klein
          // sein. Die erhoehte Grenze haette hier Flaechen zwischen 44px und
          // 110px Hoehe ausgeschlossen, auf denen ein Objekt vollstaendig
          // sichtbar Platz hat -- genau die Flaechen, die der Peek-Anteil
          // unter 100 % braucht.
          if (rect.bottom - rect.top < HuntGame.MIN_OBJECT_SIZE) continue;
          candidates.push({ rect, anchor: section });
        }
        return candidates;
      }

      collectBlockedRects() {
        const area = this.searchArea;
        if (!area) return [];
        // Kein Aufschlag auf die Sperrflaechen. Die Klickflaeche des Objekts ist
        // deckungsgleich mit dem Rechteck, das die Geometrie prueft -- nicht
        // wegen eines Hit-Testing-Verhaltens von clip-path (das gibt es in
        // diesem Feature nicht mehr), sondern weil der Button GENAU dieses
        // Rechteck IST und mit overflow:hidden beschneidet. Das ist in jeder
        // Engine gleich definiert.
        //
        // Dieses Rechteck ist die BOX aus objectBox(): ohne laufenden Puls das
        // sichtbare Rechteck, mit laufendem Puls das um PULSE_HEADROOM
        // erweiterte (die Huelle des pulsierenden Objekts). Beide Seiten --
        // Schreiben der Geometrie und Pruefung gegen diese Liste -- lesen
        // dieselbe Methode; nur deshalb gilt Design-Spec 6.6 weiter.
        //
        // Ein Aufschlag waere hier sogar
        // schaedlich: Inhaltsbilder stehen ueblicherweise in einem <a>, und ein
        // um wenige Pixel vergroesserte Sperrflaeche verbietet genau die Kante,
        // an der der Peek-Effekt hervorschauen soll -- gemessen bricht der
        // Peek-Anteil damit von ~99% auf 0% ein.
        const rects = [];
        for (const element of area.querySelectorAll(HuntGame.BLOCKED_SELECTOR)) {
          const rect = this.documentRect(element);
          if (rect.right - rect.left <= 0 || rect.bottom - rect.top <= 0) continue;
          rects.push(rect);
        }
        rects.push(this.documentRect(this));
        // Der Zaehler ist selbst Ausschlusszone (Design-Spec 11): ein Objekt
        // unter ihm waere nicht sichtbar und trotzdem klickbar. Er ist
        // `position: fixed`, sein Dokumentrechteck haengt also am aktuellen
        // Scrollstand -- gesperrt wird die Stelle, an der er JETZT steht. Der
        // Zugriff bleibt defensiv: ohne Objektbild rendert die Liquid-Seite
        // keinen Zaehler.
        const counter = this.counterElement;
        // hidden (Zustand idle/won) heisst Rechteck 0x0. Das wuerde als
        // Sperrflaeche am Dokumentanfang landen statt an der Stelle des
        // Zaehlers -- nicht schaedlich, aber irrefuehrend, deshalb ausgelassen.
        if (counter && !counter.hidden) rects.push(this.documentRect(counter));

        /*
         * FREMDE SPIELE. Unterstuetzt ist EIN Suchspiel pro Seite (Design-Spec
         * 5.2, Hinweis im Schema) -- aber der Merchant kann die Section mit
         * zwei Klicks zweimal platzieren, und dann darf das Ergebnis kein
         * Durcheinander sein. Gemessen mit zwei Instanzen auf einer Seite:
         * ein Objekt von Spiel A lag auf einem Objekt von Spiel B, und A
         * platzierte ein Objekt direkt auf der Karte von B (deren
         * .shopify-section ist fuer A ein voellig regulaerer Flaechen-Anker).
         *
         * Deshalb sperren wir Wirt, Zaehler und Objekte JEDES anderen Spiels.
         * Die Suche laeuft ueber das ganze Dokument und nicht nur ueber
         * #MainContent: die Objekt-Ebenen haengen an document.body und der
         * Zaehler ist `position: fixed`.
         *
         * Ausgeschlossen wird das Eigene: `this` samt Nachkommen (der eigene
         * Zaehler ist ein Kindknoten und steht schon oben, zustandsrichtig)
         * und die eigene Ebene mit den eigenen Objekten -- die stehen bewusst
         * NICHT in dieser Liste, sonst blockierte jedes Objekt seinen eigenen
         * Platz. Beim Ersetzen nach einem Umbruch legt replaceObject() die
         * eigenen Geschwister gezielt selbst dazu.
         *
         * Kosten: eine Selektorsuche ueber das Dokument je Erhebung. Auf
         * einer Seite mit genau einem Spiel liefert sie den eigenen Wirt und
         * die eigenen Objekte, die alle sofort wieder herausfallen.
         */
        for (const element of document.querySelectorAll(
          'hunt-game, .hunt-game__counter, .hunt-game__object'
        )) {
          if (element === this || this.contains(element)) continue;
          if (this.layer && this.layer.contains(element)) continue;
          const rect = this.documentRect(element);
          // 0x0 heisst hier "verborgen": ein fremder Zaehler im Zustand idle
          // traegt `hidden`. Sein Rechteck am Dokumentanfang waere eine
          // Sperrflaeche an der falschen Stelle.
          if (rect.right - rect.left <= 0 || rect.bottom - rect.top <= 0) continue;
          rects.push(rect);
        }
        return rects;
      }

      // --- Overlay-Ebene und Rendering.

      ensureLayer() {
        if (this.layer && this.layer.isConnected) return this.layer;
        this.layer = document.createElement('div');
        this.layer.className = 'hunt-game__layer';
        this.layer.dataset.huntGameLayer = this.settings.sectionId;
        // An document.body, NICHT in die Section: .hunt-game__card hat
        // overflow:hidden, und .hunt-game__card-stack darf kein drittes Kind
        // bekommen (zweite Grid-Zeile -> Kartenhoehe waere nicht mehr konstant).
        document.body.appendChild(this.layer);
        return this.layer;
      }

      clearLayer() {
        // Ohne Objekte gibt es nichts neu zu berechnen: Beobachtung zuerst
        // abraeumen, sonst laeuft ein bereits geplanter Lauf ins Leere.
        this.unobserveLayout();
        if (this.layer) {
          this.layer.remove();
          this.layer = null;
        }
        this.objects = [];
      }

      /**
       * Sichtbares Teilrechteck eines Objekts, abgeleitet aus `rect`,
       * `clipInset` und `size`.
       *
       * Bewusst NICHT aus `position.visibleRect` gelesen, obwohl placeObjects
       * das mitliefert: Task 10 rechnet `rect` bei einem Reflow neu und fuehrt
       * kein visibleRect mit. rect + clipInset sind die einzige Quelle, aus der
       * sich beides jederzeit konsistent ableiten laesst. clipInset ist auf
       * `size` normiert (siehe peekPlacement), deshalb wird die Kantenlaenge
       * hier aus size gerechnet und nicht aus rect.right/rect.bottom.
       */
      visibleBox(position) {
        const rect = position.rect;
        const size = position.size;
        const clip = position.clipInset;
        if (!clip) {
          return { left: rect.left, top: rect.top, right: rect.left + size, bottom: rect.top + size };
        }
        return {
          left: rect.left + (clip.left / 100) * size,
          top: rect.top + (clip.top / 100) * size,
          right: rect.left + size - (clip.right / 100) * size,
          bottom: rect.top + size - (clip.bottom / 100) * size,
        };
      }

      /**
       * Laeuft ueberhaupt eine Animation? assets/hunt-game.css schaltet den
       * Puls unter `prefers-reduced-motion: reduce` auf `animation: none`.
       */
      get pulseRuns() {
        return (
          this.settings.pulse && !window.matchMedia('(prefers-reduced-motion: reduce)').matches
        );
      }

      /**
       * Spielraum, den die Objekt-Box ueber das sichtbare Rechteck hinaus
       * braucht. Ohne laufenden Puls keiner (1) -- klickbar ist genau das
       * Sichtbare. Mit laufendem Puls genau der Hoehepunkt der Puls-Keyframe,
       * damit das Fenster dem skalierten Bild nichts abschneidet (siehe
       * PULSE_HEADROOM).
       *
       * `prefers-reduced-motion` MUSS hier mitgelesen werden. Das CSS schaltet
       * den Puls unter dieser Einstellung auf `animation: none` (siehe die
       * Media-Query am Ende von assets/hunt-game.css): der span skaliert dann
       * nie, und der Spielraum wuerde von KEINEM Pixel jemals bedeckt.
       * Gemessen mit unterdrueckter Puls-Regel: bei size 100 ein dauerhaft
       * toter Streifen von 3,00px auf allen vier Seiten, bei size 110 3,30px.
       * Er ist geprueft und kann darum keinen Theme-Klick schlucken -- aber er
       * ist klickbare Flaeche, die der Kunde nie sieht, und genau das verbietet
       * Design-Spec 6.6. Die Begruendung des Spielraums (Spec 7: "die Huelle
       * des pulsierenden Objekts ueber den ganzen Zyklus") gilt ohne Zyklus
       * nicht.
       *
       * Aendert der Kunde die Einstellung mitten in der Runde, bleibt die Box
       * bis zur naechsten Neuberechnung stehen; die naechste Layout-Aenderung
       * schreibt sie neu. Eine Box zu VERKLEINERN ist gegen Sperrflaechen und
       * Sicherheitsrand immer weiterhin gueltig, dieser Weg braucht also keine
       * erneute Pruefung.
       */
      get boxScale() {
        return this.pulseRuns ? HuntGame.PULSE_HEADROOM : 1;
      }

      /**
       * Die BOX eines Objekts: Rechteck des Buttons, also Sichtfenster UND
       * Klickflaeche. Ohne Puls identisch mit dem sichtbaren Rechteck, mit Puls
       * um den Puls-Spielraum erweitert.
       *
       * Diese Methode ist die EINZIGE Quelle fuer beide Verwendungen: das
       * Schreiben der Geometrie (applyPosition) und die Pruefung gegen die
       * Sperrflaechen (isPositionStillValid, replaceObject). Nur solange beide
       * dasselbe Rechteck benutzen, gilt die Regel aus Design-Spec 6.6 --
       * klickbar ist nur, was geprueft wurde.
       */
      objectBox(position) {
        return HuntGameGeometry.objectBox(
          this.visibleBox(position),
          position.size,
          this.boxScale,
          position.clipInset
        );
      }

      /**
       * Schreibt Fenster und Inhalt eines Objekts in einem Zug.
       *
       * `box` ist das Fenster: Groesse und Position des Buttons, der per
       * overflow:hidden Anzeige UND Klickflaeche beschneidet. Der span traegt
       * das volle Objekt und wird um die Differenz zwischen echter Position und
       * Fenster verschoben, sodass das Bild immer auf `position.rect` liegt.
       *
       * KOORDINATEN-VERTRAG: alles in DOKUMENT-Koordinaten -- genau in der Form,
       * in der placeObjects sie liefert und this.objects[].position sie
       * aufbewahrt. Die Ebene liegt bei top:0/left:0 (siehe .hunt-game__layer),
       * es wird nichts abgezogen und es gibt keinen Offset, den ein Aufrufer
       * falsch herum rechnen koennte.
       */
      writeGeometry(element, position, box) {
        element.style.transform = `translate3d(${box.left}px, ${box.top}px, 0)`;
        element.style.setProperty('--hunt-game-visible-width', `${box.right - box.left}px`);
        element.style.setProperty('--hunt-game-visible-height', `${box.bottom - box.top}px`);
        element.style.setProperty('--hunt-game-object-size', `${position.size}px`);

        const clipElement = element.querySelector('.hunt-game__object-clip');
        if (clipElement) {
          clipElement.style.setProperty('--hunt-game-offset-x', `${position.rect.left - box.left}px`);
          clipElement.style.setProperty('--hunt-game-offset-y', `${position.rect.top - box.top}px`);
        }
      }

      /**
       * Setzt ein Objekt auf seine Spielposition: sichtbar ist nur der Teil, der
       * nicht hinter dem Anker liegt. `this.applyPosition(entry.element,
       * entry.position)` ist damit immer korrekt -- auch fuer Task 10, das nur
       * `rect` neu berechnet und `size`/`clipInset` unveraendert mitgibt.
       */
      applyPosition(element, position) {
        this.writeGeometry(element, position, this.objectBox(position));
      }

      /**
       * Deckt ein Objekt vollstaendig auf: das Fenster waechst auf das ganze
       * Objektrechteck, der span-Offset geht auf 0. Ersetzt das frueher
       * genuegende Zuruecknehmen eines clip-path.
       *
       * Beim Fund VOR dem Setzen von `data-found` aufgerufen, damit der Kunde
       * erst sieht, WAS versteckt war, und die Fund-Animation danach das ganze
       * Objekt zeigt.
       *
       * Das `transform`, das hier geschrieben wird, gehoert der Positionierung
       * und darf von KEINER Animation dieses Elements beruehrt werden -- auch
       * nicht mittelbar: die eigenstaendige Property `scale` skaliert das
       * Verschieben aus `transform` mit (nachgemessen, siehe die Begruendung in
       * assets/hunt-game.css). Deshalb laufen die Animationen am span.
       */
      revealObject(element, position) {
        const rect = position.rect;
        this.writeGeometry(element, position, {
          left: rect.left,
          top: rect.top,
          right: rect.left + position.size,
          bottom: rect.top + position.size,
        });
      }

      renderObjects(positions) {
        // Ohne Template (kein Objektbild) wird nichts gerendert, statt zu werfen
        // -- und vor allem, ohne vorher eine leere Ebene an den body zu haengen.
        const template = this.objectTemplate;
        if (!template) return;
        const layer = this.ensureLayer();

        this.objects = positions.map((position, index) => {
          const fragment = template.content.cloneNode(true);
          const element = fragment.querySelector('.hunt-game__object');
          element.dataset.huntGameObject = String(index);
          if (this.settings.pulse) {
            element.dataset.huntGamePulse = 'true';
            // Als Custom Property, NICHT als Inline-animation-delay. Die
            // Animationen laufen am span (siehe assets/hunt-game.css), der
            // Versatz muss also DORT ankommen -- und `animation-delay` erbt
            // nicht. Ein Inline-Wert am Button erreichte den span damit gar
            // nicht: alle Objekte pulsierten im Gleichtakt, und genau das soll
            // der Versatz verhindern. Eine Custom Property erbt und wird von der
            // Puls-Regel am span gelesen; die Fund-Animation liest sie nicht und
            // laeuft unverzoegert an.
            element.style.setProperty('--hunt-game-pulse-delay', `${(index * 370) % 2000}ms`);
          }
          // Dokument-Koordinaten unveraendert durchgeben: die Ebene ist der
          // Nullpunkt des Dokuments (siehe applyPosition).
          this.applyPosition(element, position);
          const entry = { element, position };
          // Der Listener haengt am Abort-Signal dieses Connects. Das Element ist
          // ueber einen Reflow hinweg dasselbe (replaceObject tauscht nur die
          // Position), der Listener muss also nie neu gesetzt werden.
          element.addEventListener('click', () => this.handleObjectClick(entry), {
            signal: this.abortController.signal,
          });
          layer.appendChild(element);
          return entry;
        });
      }

      /**
       * Groessen-, Peek- und Abstandsvorgaben der Platzierung. Eine Quelle fuer
       * beide Aufrufer (erste Runde und Ersatzplatzierung nach einem Reflow) --
       * sonst driften Objektgroesse oder Ankerschwelle zwischen den Pfaden
       * auseinander.
       */
      placementConfig() {
        return {
          sizeConfig: {
            baseSize: this.isMobile ? this.settings.sizeMobile : this.settings.sizeDesktop,
            variationPercent: this.settings.sizeVariation,
            // Zwei Untergrenzen, weil placeObjects sie PRO PLATZIERUNG waehlt:
            // vollstaendig sichtbar auf einer Flaeche -> 44px genuegen; hinter
            // einer Bildkante -> das Objekt muss so gross werden, dass der
            // sichtbare Streifen die 44px erreicht.
            minSize: HuntGame.MIN_OBJECT_SIZE,
            minSizePeek: this.peekMinObjectSize(),
          },
          peekConfig: {
            enabled: this.settings.imagePeek,
            peekPercent: this.settings.imagePeekPercent,
            share: this.settings.imagePeekShare,
          },
          minDistance: this.settings.minDistance,
          safeMargin: HuntGame.SAFE_MARGIN,
          minImageSize: this.effectiveMinImageSize(),
          boxScale: this.boxScale,
          maxAttempts: 30,
          random: Math.random,
        };
      }

      // --- Neuberechnung bei Layout-Aenderungen (Design-Spec 6.7).

      /**
       * Rechnet eine Position aus ihrem Anker neu. Zurueck kommt eine KOPIE mit
       * frischem `rect` und `visibleRect`; `size`, `clipInset`, `anchor`, die
       * Ratios und die Kante bleiben unveraendert -- es wird NICHT neu
       * gewuerfelt, niemand soll mitten im Spiel sein Objekt verlieren.
       *
       * Alles in DOKUMENT-Koordinaten, genau wie writeGeometry es erwartet: die
       * Ebene liegt bei top:0/left:0, es wird nichts abgezogen.
       *
       * ratioX/ratioY koennen negativ sein (Peek an der oberen oder linken
       * Kante: das Objektrechteck liegt dann vor dem Anker) und werden bewusst
       * nicht geklemmt.
       */
      positionFromAnchor(position) {
        const anchor = position.anchor;
        if (!anchor || !anchor.isConnected) return null;
        const anchorRect = this.documentRect(anchor);
        const anchorWidth = anchorRect.right - anchorRect.left;
        const anchorHeight = anchorRect.bottom - anchorRect.top;

        /*
         * Peek-Objekte werden ueber die KANTE rekonstruiert, nicht ueber die
         * skalierte Ratio -- der Fehler aus dem Geraetetest (abgeschnittene
         * Objekte in Bildern).
         *
         * Die Ratios beschreiben den Abstand zum Ankerrand als ANTEIL der
         * Ankergroesse. Fuer ein Objekt auf einer freien Flaeche ist das
         * richtig: es soll sich mit der Flaeche mitbewegen und mitskalieren.
         * Fuer ein Peek-Objekt ist es falsch, denn es haengt an einer Kante:
         * aendert der Anker seine Groesse -- spaet geladenes Bild, Umbruch,
         * andere Schrifthoehe --, wandert das Objekt anteilig mit, waehrend die
         * Kante an ihrer neuen Stelle steht. Der Beschnitt blieb unveraendert
         * und verdeckte weiter dieselbe Menge. Sichtbar blieb eine
         * Objekt-Scheibe im Bild oder eine, die darunter schwebte.
         *
         * Aus den Ratios kommt darum nur noch die Position ENTLANG der Kante;
         * quer dazu setzt peekPlacementAtEdge das Objekt an die frisch
         * gemessene Kante und rechnet Beschnitt und sichtbares Rechteck
         * gemeinsam neu.
         *
         * Warum die Ratio entlang der Kante bleiben DARF: dort ist ein
         * Anteil die richtige Beschreibung -- ein Objekt in der Mitte der
         * Unterkante soll in der Mitte bleiben, wenn das Bild breiter wird.
         * Nur quer zur Kante ist der Anteil bedeutungslos.
         */
        if (position.clipInset && position.edge) {
          const horizontal = position.edge === 'left' || position.edge === 'right';
          const alongPos = horizontal
            ? anchorRect.top + position.ratioY * anchorHeight
            : anchorRect.left + position.ratioX * anchorWidth;
          const placement = HuntGameGeometry.peekPlacementAtEdge({
            imageRect: anchorRect,
            size: position.size,
            peekPercent: this.settings.imagePeekPercent,
            edge: position.edge,
            alongPos,
          });
          return Object.assign({}, position, {
            rect: placement.rect,
            visibleRect: placement.visibleRect,
            clipInset: placement.clipInset,
          });
        }

        const left = anchorRect.left + position.ratioX * anchorWidth;
        const top = anchorRect.top + position.ratioY * anchorHeight;
        const next = Object.assign({}, position, {
          rect: { left, top, right: left + position.size, bottom: top + position.size },
        });
        // visibleRect wird mitgefuehrt, obwohl dieser Code es nirgends liest
        // (visibleBox() leitet das sichtbare Rechteck bewusst immer aus rect,
        // clipInset und size ab). Grund: `visibleRect` gehoert zur
        // dokumentierten Form von `position`, die placeObjects liefert und
        // this.objects[].position aufbewahrt -- Tasks 11-13 duerfen es lesen.
        // Eine Kopie mit frischem rect und altem visibleRect waere die
        // schlimmere Variante: still falsch statt offensichtlich abwesend.
        next.visibleRect = this.visibleBox(next);
        return next;
      }

      /**
       * Ist der Anker ein Karussell-Bild, dessen Folie aus dem sichtbaren
       * Ausschnitt gewandert ist? Getrennte Methode, weil der Grund der
       * Ungueltigkeit die Wahl der Ersatzposition beeinflusst (siehe
       * replaceObject).
       */
      slideAnchorOutOfView(position) {
        const anchor = position.anchor;
        return !!(
          anchor &&
          anchor.tagName === 'IMG' &&
          anchor.isConnected &&
          !this.isSlideInView(anchor)
        );
      }

      /**
       * Ist eine neu berechnete Position noch zulaessig?
       *
       * Geprueft wird die BOX des Objekts -- dasselbe Rechteck, das die
       * Geometrie bei der Platzierung geprueft hat, und seit dem Beschnitt per
       * overflow:hidden per Konstruktion die Klickflaeche des Objekts. Bei
       * aktivem Puls schliesst sie den Puls-Spielraum ein (objectBox); das ist
       * gerade der Punkt: geprueft werden muss, was klickbar ist.
       *
       * minDistance wird hier bewusst NICHT mitgeprueft (placedCenters bleibt
       * leer): Anlass der erneuten Pruefung ist die Klick-Uebernahme. Ein
       * Reflow, der zwei Objekte etwas naeher zusammenschiebt, ist kein
       * Sicherheitsproblem, wuerde aber bei jedem Umbruch Objekte umsetzen und
       * damit die Positionsstabilitaet ohne Gegenwert aufgeben.
       */
      isPositionStillValid(position, areaRect, blockedRects) {
        // Anker weg (Section per Section-Rendering-API ersetzt): das Objekt
        // klebt dann an einer festen Pixelstelle und wandert bei keinem
        // weiteren Umbruch mehr mit. Lieber neu setzen.
        if (!position.anchor || !position.anchor.isConnected) return false;
        // Karussell: ein Folienwechsel verschiebt den Anker, ohne seine Groesse
        // zu aendern. Ist die Folie aus dem Ausschnitt gewandert, ist sie kein
        // brauchbarer Anker mehr -- das Objekt haengt sonst neben dem Karussell
        // in der Luft (die Ebene liegt an document.body, das overflow:hidden des
        // .swiper-Containers greift dort nicht).
        if (this.slideAnchorOutOfView(position)) return false;
        return HuntGameGeometry.isPositionValid({
          rect: this.objectBox(position),
          blockedRects,
          placedCenters: [],
          minDistance: 0,
          areaRect,
          safeMargin: HuntGame.SAFE_MARGIN,
        });
      }

      /**
       * Setzt GENAU EIN Objekt neu, weil seine neu berechnete Position nicht
       * mehr zulaessig ist. Alle anderen bleiben liegen, `total` bleibt
       * unveraendert -- das ist eine Verschiebung, keine neue Runde. Auch das
       * Element bleibt dasselbe: Klick-Listener und Fund-Zustand haengen daran.
       *
       * Rueckgabe: false, wenn sich auf der ganzen Seite kein zulaessiger Platz
       * mehr findet.
       */
      replaceObject(entry, context, avoidSlides) {
        // Die uebrigen Objekte kommen als Sperrflaechen dazu, damit die
        // Ersatzposition nicht auf einem anderen Objekt landet. In
        // collectBlockedRects() stehen sie nicht: die Ebene haengt an
        // document.body, gesammelt wird nur innerhalb von #MainContent.
        const others = [];
        for (const other of this.objects) {
          // Die BOX der anderen Objekte, nicht ihr sichtbares Rechteck: bei
          // aktivem Puls ist die Box der tatsaechliche Fussabdruck, und die
          // Ersatzposition darf nicht in den Puls-Spielraum eines Nachbarn.
          if (other !== entry) others.push(this.objectBox(other.position));
        }
        const blockedRects = context.blockedRects.concat(others);

        // Wurde das Objekt ungueltig, WEIL seine Karussell-Folie aus dem
        // Ausschnitt gewandert ist, dann ist die naechste Folie ein schlechter
        // Ersatz: der naechste Autoplay-Tick verschiebt es wieder. Gemessen
        // landeten 108 von 200 Ersatzplatzierungen erneut auf einem
        // Folienbild. Deshalb zuerst OHNE Folienbilder versuchen -- und nur
        // dann mit ihnen, wenn sonst nichts zu finden ist: ein umgesetztes
        // Objekt auf einer Folie ist besser als ein geparktes.
        const attempts = [];
        if (avoidSlides) {
          const withoutSlides = context.imageCandidates.filter(
            (candidate) => !candidate.anchor.closest('.swiper-slide')
          );
          if (withoutSlides.length !== context.imageCandidates.length) {
            attempts.push(withoutSlides);
          }
        }
        attempts.push(context.imageCandidates);

        // Der Charakter des Objekts bleibt erhalten: was hinter einem Bild lag,
        // sucht wieder zuerst ein Bild, was auf einer freien Flaeche lag, wieder
        // eine Flaeche. Ohne diesen Plan waere jede Ersatzplatzierung ein
        // Peek-Slot (placeObjects mit count 1 kommt wegen der Mindestvorgabe
        // immer auf ein Peek-Ziel), und der eingestellte Anteil driftete mit
        // jeder Fensteraenderung nach oben.
        const peekPlan = [entry.position.clipInset !== null];

        for (const imageCandidates of attempts) {
          const config = this.placementConfig();
          config.peekConfig = Object.assign({}, config.peekConfig, { plan: peekPlan });
          const result = HuntGameGeometry.placeObjects(
            Object.assign(
              {
                count: 1,
                areaRect: context.areaRect,
                imageCandidates,
                surfaceCandidates: context.surfaceCandidates,
                blockedRects,
              },
              config
            )
          );
          if (result.total === 0) continue;
          entry.position = result.positions[0];
          this.applyPosition(entry.element, entry.position);
          return true;
        }
        return false;
      }

      /**
       * Parkt ein Objekt: unsichtbar und inert, aber weiter in `this.objects`
       * und weiter in `total`. Das naechste Layout-Signal versucht es erneut.
       *
       * Ausdruecklich NICHT: Objekt entfernen und `total` senken. Genau das war
       * der schwerste Defekt dieses Features (Review-Runde 1): bei 2 von 8
       * gefundenen Objekten und sechs unplatzierbaren Objekten fiel `total` auf
       * 2, damit galt die Runde als gewonnen, und `handleWin()` lief. Spaet in
       * einer Runde reicht dafuer ein einziges uebriges Objekt. Task 11
       * schreibt "gewonnen" in den localStorage -- der Fehlgriff waere also
       * unumkehrbar: ein Ziehen am Fenster haette den Rabattcode dauerhaft
       * freigegeben. Deshalb gilt jetzt (Design-Spec 6.7): der Reflow-Pfad
       * gewinnt nie. `handleWin()` ist von `repositionObjects()` aus nicht
       * erreichbar, und eine Runde, die unvollendbar bleibt, weil kein
       * zulaessiger Platz existiert, ist das ehrliche Scheitern -- dasselbe,
       * was `buildRound()` beim Rundenstart ohnehin tut.
       *
       * `visibility: hidden` statt `display: none`: das Element behaelt seine
       * Box, die Geometrie bleibt messbar und das Freigeben ist ein reines
       * Zuruecknehmen. Gesetzt wird ein Attribut, nicht ein Inline-Style: den
       * Inline-`pointer-events` des Buttons schreibt Task 11 beim Fund, und
       * beide Seiten wuerden sich sonst gegenseitig ueberschreiben.
       */
      parkObject(entry) {
        if (entry.parked) return;
        entry.parked = true;
        entry.element.dataset.huntGameParked = 'true';
      }

      releaseObject(entry) {
        if (!entry.parked) return;
        entry.parked = false;
        delete entry.element.dataset.huntGameParked;
      }

      /**
       * Rechnet die Objektpositionen aus ihren Ankern neu und prueft sie erneut
       * gegen die Sperrflaechen.
       */
      repositionObjects() {
        const area = this.searchArea;
        if (!area || !this.layer || !this.objects || this.objects.length === 0) return;

        // Dasselbe Messgatter wie in buildRound(). Solange data-initializing am
        // body haengt, gibt theme.css jeder dritten und spaeteren Section in
        // #MainContent `content-visibility: auto` ohne contain-intrinsic-size:
        // sie messen als Hoehe 0, es gibt praktisch keine Kandidaten, und jede
        // Position faellt aus dem geschrumpften Suchbereich. Eine Neuberechnung
        // in diesem Moment wuerde also alles fuer ungueltig erklaeren und alle
        // Objekte parken. Kein Warten wie in buildRound(): die Freigabe von
        // content-visibility laesst #MainContent wachsen, der ResizeObserver
        // feuert dadurch von selbst.
        if (!this.canMeasure) return;

        const areaRect = this.documentRect(area);

        // 1. Pixel aus den Ankern neu rechnen und sofort schreiben.
        for (const entry of this.objects) {
          const next = this.positionFromAnchor(entry.position);
          // Anker weg: alte Position stehen lassen, Schritt 3 setzt das Objekt
          // ohnehin neu (isPositionStillValid verwirft einen toten Anker).
          if (!next) continue;
          entry.position = next;
          this.applyPosition(entry.element, next);
        }

        // 2. Sperrflaechen FRISCH erheben. Nach dem Umbruch stehen Links und
        //    Buttons woanders, und genau das ist hier der Punkt.
        const blockedRects = this.collectBlockedRects();

        // 3. Jede neu berechnete Position erneut validieren (Spec 6.7). Ein
        //    Reflow verschiebt das Objekt MIT seinem Anker, aber die uebrigen
        //    Elemente bewegen sich relativ dazu -- eine vorher gueltige Position
        //    kann dadurch auf einem Link oder Button liegen. Das ist dieselbe
        //    Klick-Uebernahme, die Spec 6.6 verbietet, nur zeitversetzt.
        //    Sicherheit sticht Positionsstabilitaet: ein Objekt, das nach einem
        //    Umbruch an anderer Stelle auftaucht, ist ein Aergernis; ein Objekt,
        //    das den Klick auf eine Kategorie-Kachel schluckt, kostet Umsatz.
        const invalid = this.objects.filter(
          (entry) => !this.isPositionStillValid(entry.position, areaRect, blockedRects)
        );

        // Wer (wieder) gueltig liegt, ist auf jeden Fall sichtbar. Das gibt ein
        // geparktes Objekt frei, sobald ein Umbruch seinen Platz freigemacht
        // hat -- ohne es umzusetzen, denn seine Ankerposition stimmt ja wieder.
        for (const entry of this.objects) {
          if (invalid.indexOf(entry) === -1) this.releaseObject(entry);
        }
        if (invalid.length === 0) return;

        // Kandidaten erst hier sammeln: im Normalfall ist nichts ungueltig, und
        // die Sammlung kostet ein Layout ueber alle Bilder des Suchbereichs.
        const context = {
          areaRect,
          blockedRects,
          imageCandidates: this.collectImageCandidates(),
          surfaceCandidates: this.collectSurfaceCandidates(),
        };
        // Eine Seite ohne Kandidaten ist ein Messproblem, kein Platzproblem:
        // ohne Kandidaten kann placeObjects gar nichts finden. Das Objekt wird
        // geparkt und beim naechsten Signal erneut versucht.
        //
        // Der Guard steht AUSSCHLIESSLICH fuer diese Absicht, er spart nichts
        // Messbares: die beiden collect-Aufrufe darueber laufen ohnehin, das
        // Layout ist also schon erzwungen. Uebersprungen wird nur ein
        // placeObjects-Aufruf ueber zwei leere Arrays und eine
        // visibleBox-Schleife -- reine Arithmetik. Und seit dem Parken ist das
        // Ergebnis mit und ohne Guard identisch, weil der zerstoerende Zweig
        // weg ist.
        const hasCandidates =
          context.imageCandidates.length > 0 || context.surfaceCandidates.length > 0;

        for (const entry of invalid) {
          const avoidSlides = this.slideAnchorOutOfView(entry.position);
          if (hasCandidates && this.replaceObject(entry, context, avoidSlides)) {
            this.releaseObject(entry);
            continue;
          }
          // Kein zulaessiger Platz: parken, nicht entfernen. `total` bleibt
          // unveraendert, updateCounter() und handleWin() werden hier NICHT
          // gerufen -- siehe parkObject().
          this.parkObject(entry);
        }
      }

      scheduleReposition() {
        window.clearTimeout(this.repositionTimer);
        this.repositionTimer = window.setTimeout(
          () => this.repositionObjects(),
          HuntGame.REFLOW_DEBOUNCE
        );
      }

      /**
       * Verwirft die eine Lieferung des ResizeObservers, die von der
       * Viewport-Hoehe kommt statt von einem echten Umbruch.
       *
       * DIE ABHAENGIGKEIT, DIE HIER BENANNT GEHOERT: #MainContent ist in diesem
       * Theme `flex: 1` in `.site-wrapper` (assets/search.css:123-131), und
       * `.site-wrapper` traegt `min-height: 100vh` (Klasse `.min-height-screen`,
       * assets/theme.css, gesetzt in layout/theme.liquid:144). Auf einer Seite,
       * die kuerzer als der Viewport ist, streckt der Viewport #MainContent
       * also -- eine Aenderung der Viewport-HOEHE aendert dann seine
       * Content-Box, ohne dass sich die Breite bewegt. Der Breitenfilter am
       * resize-Listener sieht das nicht: er sitzt am Ereignis, nicht am
       * Observer. Genau dieser Weg umgeht ihn.
       *
       * Auf iOS ist der Fall heute nur deshalb harmlos, weil das Theme `vh`
       * benutzt: 100vh loest gegen den GROSSEN Viewport auf und bewegt sich beim
       * Einklappen der Adressleiste nicht. Im ganzen Theme gibt es kein `dvh`,
       * `svh` oder `lvh` (gegengeprueft in sections/, snippets/ und den
       * CSS-Dateien). Ein einziges `100vh` -> `100dvh` irgendwo wuerde das
       * Zucken zurueckbringen, gegen das diese Task existiert. Deshalb filtert
       * der Observer selbst und verlaesst sich nicht auf die Einheit.
       *
       * Die Hoehe bleibt ein gueltiger Ausloeser -- echt nachgewachsener Inhalt
       * MUSS die Objekte bewegen. Verworfen wird nur, wenn die Breite gleich
       * bleibt, die Hoehenaenderung der des Viewports entspricht UND die
       * Viewport-Aenderung frisch ist.
       *
       * Die AKTUALITAETSSCHRANKE ist nicht kosmetisch. Die Vergleichsbasis
       * lastViewportHeight wird nur bei einer Lieferung nachgezogen -- auf einer
       * langen Seite aendert eine Viewport-Hoehenaenderung die Content-Box aber
       * gar nicht, es kommt also keine Lieferung und die Basis bleibt stehen.
       * Gemessen (Review-Runde 2): Basis 1200 gegen innerHeight 1501, danach
       * wuchs eine Section um genau 301px -- ein echter Inhalts-Reflow. Beide
       * Deltas sahen gleich aus, die Lieferung wurde verworfen, und fuenf von
       * sechs Objekten standen 301px neben ihrem Anker, ohne dass die
       * Klickziel-Pruefung gelaufen waere. Eine Lieferung, die lange nach jeder
       * Viewport-Aenderung eintrifft, ist per Definition eine Inhaltsaenderung.
       *
       * Der Zeitstempel wird bewusst im resize-Listener gefuehrt und
       * lastViewportHeight dort NICHT nachgezogen: das resize-Ereignis kommt vor
       * der Lieferung, ein Nachziehen wuerde viewportDelta auf 0 rechnen und der
       * Verwurf wuerde nie greifen.
       *
       * GEMESSENE GRENZE, bewusst offen: der Verwurf greift nur, solange die
       * Content-Box an BEIDEN Enden vom Viewport getrieben ist. Ein Schritt von
       * 1100 auf 700 auf einer Seite mit 914px natuerlicher Inhaltshoehe ergab
       * Box 1100.38 -> 914.15, also Viewport-Delta -400 gegen Box-Delta -186:
       * nicht verworfen, eine Neuberechnung lief. Die Objekte bewegten sich
       * dabei nicht, es ist also ein verlorener Layout-Durchlauf und kein
       * Sprung -- auf iOS mit `dvh` und einer Seite, deren natuerliche Hoehe
       * genau im Band der Adressleiste liegt, kaeme das Zucken damit zurueck.
       */
      isViewportHeightResize(observerEntry) {
        const viewportHeight = window.innerHeight;
        const previousViewportHeight = this.lastViewportHeight;
        const previousBox = this.lastContentBox;
        const box =
          observerEntry && observerEntry.contentRect
            ? { width: observerEntry.contentRect.width, height: observerEntry.contentRect.height }
            : null;
        this.lastViewportHeight = viewportHeight;
        this.lastContentBox = box;
        // Erste Lieferung (die des observe() selbst): nie filtern, es gibt
        // keinen Vergleichswert.
        if (!box || !previousBox) return false;
        if (Math.abs(box.width - previousBox.width) > 0.5) return false;
        const boxDelta = box.height - previousBox.height;
        const viewportDelta = viewportHeight - previousViewportHeight;
        if (Math.abs(boxDelta) <= 0.5) return false;
        if (Math.abs(boxDelta - viewportDelta) > 1) return false;
        // Aktualitaetsschranke: nur eine Lieferung innerhalb eines Debounce
        // nach einer echten Viewport-Hoehenaenderung darf verworfen werden.
        return Date.now() - this.lastViewportChangeAt <= HuntGame.REFLOW_DEBOUNCE;
      }

      observeLayout() {
        const area = this.searchArea;
        if (!area) return;
        // Idempotent: buildRound() laeuft bei jedem Start und Neustart. Ohne
        // dieses Abraeumen sammelten sich ResizeObserver und Folien-Listener.
        this.unobserveLayout();

        this.lastDocumentWidth = document.documentElement.clientWidth;
        // Vergleichswerte fuer isViewportHeightResize(). null erzwingt, dass
        // die erste Lieferung des neuen Observers ungefiltert durchgeht.
        this.lastContentBox = null;
        this.lastViewportHeight = window.innerHeight;
        // Getrennt gefuehrt: lastEventViewportHeight ist die Sicht des
        // resize-Listeners und dient NUR dazu, eine Hoehenaenderung zu erkennen
        // und zu datieren. lastViewportHeight ist die Vergleichsbasis der
        // Lieferungen. Die beiden duerfen nicht zusammenfallen, siehe
        // isViewportHeightResize().
        this.lastEventViewportHeight = window.innerHeight;
        this.lastViewportChangeAt = 0;

        // Primaersignal: die Content-Box von #MainContent. Sie ist von der ein-
        // und ausklappenden iOS-Adressleiste weitgehend unberuehrt -- die
        // Adressleiste veraendert den Viewport, nicht den Inhalt. "Weitgehend",
        // weil dieses Theme #MainContent auf kurzen Seiten vom Viewport
        // strecken laesst; diesen einen Weg filtert isViewportHeightResize()
        // heraus. Der Observer feuert damit bei echten Umbruechen:
        // Breitenwechsel, nachgeladene Bilder, freigegebenes content-visibility.
        this.resizeObserver = new ResizeObserver((entries) => {
          if (this.isViewportHeightResize(entries[entries.length - 1])) return;
          this.scheduleReposition();
        });
        this.resizeObserver.observe(area);

        // Karussell: ein Folienwechsel (Autoplay oder Wischen) verschiebt den
        // Anker OHNE Groessenaenderung -- der ResizeObserver feuert dafuer nie.
        // Swiper translatiert .swiper-wrapper mit einer CSS-Transition; deren
        // Ende ist das praezise Signal. Der Wrapper ist in diesem Theme
        // serverseitig gerendert, existiert also auch vor der Initialisierung.
        this.slideHandler = (event) => {
          // Nur die Verschiebung des Wrappers selbst. transitionend blubbert,
          // sonst wuerde jede Hover-Transition im Karussell eine Neuberechnung
          // ausloesen.
          if (event.propertyName !== 'transform') return;
          if (event.target !== event.currentTarget) return;
          // Und nur, wenn wirklich ein Anker in diesem Karussell haengt: sonst
          // rechnet jedes Autoplay auf der Seite dauernd die Objekte neu.
          const wrapper = event.currentTarget;
          const affected = this.objects.some(
            (entry) => entry.position.anchor && wrapper.contains(entry.position.anchor)
          );
          if (affected) this.scheduleReposition();
        };
        this.slideWrappers = [];
        for (const wrapper of area.querySelectorAll('.swiper-wrapper')) {
          if (this.contains(wrapper)) continue;
          wrapper.addEventListener('transitionend', this.slideHandler, {
            passive: true,
            signal: this.abortController.signal,
          });
          this.slideWrappers.push(wrapper);
        }

        if (this.layoutListenersBound) return;
        this.layoutListenersBound = true;
        const signal = this.abortController.signal;

        // Sekundaersignal: NUR Aenderungen der DOKUMENTBREITE. iOS Safari feuert
        // resize beim Kollabieren der URL-Leiste waehrend des Scrollens; eine
        // ungefilterte Neuberechnung liefe dann mitten im Scrollen und liesse
        // die Objekte zucken. Hoehen-only-Aenderungen werden verworfen, und
        // visualViewport wird bewusst gar nicht abgehoert.
        window.addEventListener(
          'resize',
          () => {
            // Zeitstempel fuer die Aktualitaetsschranke von
            // isViewportHeightResize(). Er wird VOR dem Breitenfilter gesetzt,
            // denn der Fall, um den es dort geht, ist genau der OHNE
            // Breitenaenderung -- und lastViewportHeight wird hier bewusst
            // nicht nachgezogen (siehe dort).
            if (window.innerHeight !== this.lastEventViewportHeight) {
              this.lastEventViewportHeight = window.innerHeight;
              this.lastViewportChangeAt = Date.now();
            }
            const width = document.documentElement.clientWidth;
            if (width === this.lastDocumentWidth) return;
            this.lastDocumentWidth = width;
            this.scheduleReposition();
          },
          { passive: true, signal }
        );

        // Drehen aendert die Breite immer. Der ResizeObserver feuert dabei zwar
        // ebenfalls, aber das Ereignis ist das direktere Signal und kostet
        // nichts: es teilt sich den Timer mit allen anderen.
        //
        // lastDocumentWidth wird hier bewusst NICHT geschrieben: manche
        // iOS-Versionen melden zum Zeitpunkt von orientationchange noch die
        // Breite VOR der Drehung. Der Wert waere dann falsch gelatcht, und das
        // unmittelbar folgende resize mit der echten neuen Breite wuerde als
        // "keine Aenderung" verworfen. Der resize-Listener pflegt den Wert
        // selbst, das genuegt.
        window.addEventListener(
          'orientationchange',
          () => this.scheduleReposition(),
          { passive: true, signal }
        );
      }

      unobserveLayout() {
        if (this.resizeObserver) {
          this.resizeObserver.disconnect();
          this.resizeObserver = null;
        }
        if (this.slideHandler && this.slideWrappers) {
          for (const wrapper of this.slideWrappers) {
            wrapper.removeEventListener('transitionend', this.slideHandler);
          }
        }
        this.slideWrappers = [];
        window.clearTimeout(this.repositionTimer);
        this.repositionTimer = null;
      }

      buildRound() {
        const area = this.searchArea;
        const template = this.objectTemplate;
        if (!area || !template) {
          // Ohne Suchbereich oder ohne Objektbild gibt es nichts zu platzieren.
          // Kein Fehler, nur zurueck in den Ausgangszustand -- und total muss
          // auch auf diesem Weg stimmen: es liegt kein Objekt auf der Seite,
          // also ist nur das Gefundene erreichbar. Bliebe hier objectCount
          // stehen, waere der Counter nie erfuellbar.
          this.clearLayer();
          this.total = this.foundCount;
          this.state = 'idle';
          this.updateCounter();
          return;
        }

        // Vor dem Messgatter aufraeumen: eine Runde, die neu gebaut wird, darf
        // keine Objekte der alten Runde stehen lassen, waehrend auf
        // Messbereitschaft gewartet wird.
        this.clearLayer();

        // Messgatter: vor `load` stehen Bildgroessen und damit die
        // Ankerrechtecke nicht fest, und solange data-initializing am body
        // haengt, sind die unteren Sections groessenbeschraenkt (siehe
        // canMeasure). Beides wuerde die Objekte an den Seitenanfang draengen.
        if (!this.canMeasure) {
          this.waitForLayout();
          return;
        }

        const remaining = Math.max(0, this.settings.objectCount - this.foundCount);
        if (remaining === 0) {
          this.total = this.settings.objectCount;
          this.handleWin();
          return;
        }

        // Groessen, Peek und Abstand kommen aus placementConfig(), damit die
        // Ersatzplatzierung nach einem Reflow mit genau denselben Vorgaben
        // rechnet (siehe replaceObject).
        const result = HuntGameGeometry.placeObjects(
          Object.assign(
            {
              count: remaining,
              areaRect: this.documentRect(area),
              imageCandidates: this.collectImageCandidates(),
              surfaceCandidates: this.collectSurfaceCandidates(),
              blockedRects: this.collectBlockedRects(),
            },
            this.placementConfig()
          )
        );

        // total ist die TATSAECHLICH platzierte Anzahl plus die bereits
        // gefundenen. Sonst entsteht ein Spielziel, das nie erreichbar ist.
        this.total = this.foundCount + result.total;

        if (result.total === 0) {
          // Es liess sich kein einziges Objekt platzieren (sehr kurze Seite,
          // fast alles Sperrflaeche). Zwei Faelle:
          if (this.foundCount > 0) {
            // Mitten im Spiel: der Rest ist unerreichbar -> als gewonnen werten,
            // statt den Kunden vor einem Counter sitzen zu lassen, der nie aufgeht.
            this.handleWin();
          } else {
            this.state = 'idle';
          }
          return;
        }

        this.renderObjects(result.positions);
        this.updateCounter();
        // Erst jetzt beobachten: vorher gibt es nichts, was mitwandern muesste.
        this.observeLayout();
      }

      // --- Fund-Interaktion, Zaehler und Gewinn (Design-Spec 7).

      /**
       * Beschriftet den Zaehler und blendet ihn zustandsabhaengig ein.
       *
       * Verglichen wird immer gegen `this.total`, NIE gegen
       * settings.objectCount: buildRound() korrigiert total nach unten, wenn
       * sich nicht alle Objekte platzieren liessen. Ein Zaehler, der sein Ziel
       * nie erreichen kann, ist der schlimmste denkbare Fehler dieses Features
       * (Design-Spec 12).
       *
       * In den Zustaenden idle und won ist `total` nicht belastbar -- buildRound()
       * setzt es auf dem Ausstiegspfad "kein Objekt platzierbar" bewusst auf
       * foundCount, unter Umstaenden also auf 0. Genau deshalb ist der Zaehler
       * dort ausgeblendet, und genau deshalb prueft handleObjectClick() den
       * Gewinn nur im Zustand playing.
       *
       * split/join statt String.replace: replace ersetzt nur das ERSTE Vorkommen.
       * Liquids `replace` (Anfangstext, serverseitig) ersetzt alle -- ein Template
       * wie "[[found]] von [[total]] ([[found]] gesammelt)" saehe sonst vor und
       * nach dem ersten Fund unterschiedlich aus. replaceAll waere die kuerzere
       * Form, ist aber erst ab iOS 13.4 verfuegbar.
       */
      updateCounter() {
        const counter = this.counterElement;
        if (!counter) return;
        counter.dataset.position = this.settings.counterPosition;
        counter.hidden = this.state !== 'playing';
        counter.textContent = this.settings.counterTemplate
          .split('[[found]]')
          .join(String(this.foundCount))
          .split('[[total]]')
          .join(String(this.total));
      }

      /**
       * Laesst den Zaehler bei jedem Fund einmal aufpoppen. Das Attribut wird
       * zuerst entfernt und nach einem erzwungenen Reflow neu gesetzt, sonst
       * startet die Animation beim zweiten Fund nicht erneut.
       */
      pulseCounter() {
        const counter = this.counterElement;
        if (!counter) return;
        counter.removeAttribute('data-pulse');
        void counter.offsetWidth;
        counter.dataset.pulse = 'true';
      }

      /**
       * Ein Fund.
       *
       * Reihenfolge ist hier ueberall Absicht:
       *
       * 1. `this.objects` SOFORT bereinigen -- nicht erst beim DOM-Aufraeumen.
       *    Zwei unabhaengige Fehler haengen daran:
       *    a) repositionObjects() und buildRound() rechnen `total` bzw. den
       *       Gewinn gegen `foundCount + this.objects.length`. Ein gefundener,
       *       aber noch nicht ausgeraeumter Eintrag wird doppelt gezaehlt --
       *       einmal in foundCount, einmal in objects.length -- und der Zaehler
       *       wird unerfuellbar.
       *    b) Ein Umbruch innerhalb der ~450ms Animationszeit wuerde das
       *       animierte Objekt sonst PARKEN. revealObject() weitet das Fenster
       *       des Buttons vom sichtbaren auf das volle Rechteck; damit
       *       ueberlappt es eine Sperrflaeche LEICHTER als vorher, die
       *       Gueltigkeitspruefung schlaegt fuer ein frisch gefundenes Objekt
       *       also FRUEHER fehl -- und der Zweig, in dem sie landet, setzt
       *       visibility:hidden. Das Objekt waere mitten im Pop verschwunden.
       * 2. `revealObject()` VOR `data-found`: es schreibt die Vollgeometrie in
       *    einem Zug (Fenster auf position.rect, span-Offset auf 0). Wuerde die
       *    Animation das noch beschnittene Objekt skalieren, wuechse das
       *    FENSTER -- der Pop saehe aus wie ein sich oeffnender Rollladen statt
       *    wie ein wachsendes Objekt.
       * 3. Der Zaehler wird HIER erhoeht, nicht in `animationend`: iOS pausiert
       *    Animationen in Hintergrund-Tabs, das Ereignis kann ausbleiben
       *    (Design-Spec 10.7).
       */
      handleObjectClick(entry) {
        const element = entry.element;
        // Doppeltipp: der Zustandswechsel ist synchron, der zweite Klick des
        // Doppeltipps sieht ihn also in jedem Fall.
        if (element.dataset.found) return;
        // Ein geparktes Objekt ist unsichtbar (Task 10). Es kann per Zeiger und
        // per Tastatur nicht getroffen werden; ein Fund waere trotzdem einer,
        // den der Kunde nicht sehen konnte.
        if (entry.parked) return;
        // Nur im laufenden Spiel. Ausserhalb ist `total` nicht belastbar (siehe
        // updateCounter), und der Gewinn darf nie aus einem anderen Zustand
        // heraus zugeteilt werden.
        if (this.state !== 'playing') return;

        const index = this.objects.indexOf(entry);
        if (index !== -1) this.objects.splice(index, 1);

        this.revealObject(element, entry.position);
        // Der Phasenversatz des Pulses muss hier NICHT zurueckgenommen werden:
        // er steckt in einer Custom Property, die nur die Puls-Regel liest
        // (siehe renderObjects). Die Fund-Animation startet damit ohne Verzug.
        element.dataset.found = 'true';
        // Zusaetzlich zur CSS-Regel fuer [data-found], damit die Klickflaeche
        // ohne Umweg ueber eine Stilneuberechnung weg ist. Inline `none` ist
        // unbedenklich; ein inline `auto` waere es NICHT -- es wuerde die
        // Park-Regel von Task 10 (Attribut, Spezifitaet 0,2,0) ueberstimmen und
        // ein geparktes Objekt wieder klickbar machen.
        element.style.pointerEvents = 'none';

        this.foundCount += 1;
        this.writeFound(this.foundCount);
        this.updateCounter();
        this.pulseCounter();

        this.removeObject(entry);

        // Der zweite legitime Aufrufer von handleWin() neben buildRound().
        // Drei Bedingungen, die sich unter der Invariante
        // total === foundCount + objects.length gegenseitig stuetzen: ist eine
        // von ihnen verletzt, wird KEIN Gewinn zugeteilt. Das ist die richtige
        // Richtung -- der Gewinn wandert in den localStorage und ein Fehlgriff
        // waere unumkehrbar. Ein geparktes Objekt haelt die Runde damit offen
        // (Design-Spec 6.7).
        if (this.total > 0 && this.objects.length === 0 && this.foundCount >= this.total) {
          this.handleWin();
        }
      }

      /**
       * Raeumt das gefundene Element aus dem DOM. `this.objects` ist zu diesem
       * Zeitpunkt bereits bereinigt (siehe handleObjectClick) -- hier passiert
       * nichts mehr, was den Spielstand beruehrt.
       *
       * Zwei Ausloeser, was zuerst kommt gewinnt, und beides ist idempotent:
       * `animationend` im Normalfall, ein Timer als Sicherheitsnetz fuer den
       * Fall, dass das Ereignis ausbleibt (Hintergrund-Tab auf iOS, oder gar
       * keine Animation, weil das CSS nicht geladen ist).
       *
       * Der Listener haengt am Button, die Animation laeuft am span darin --
       * `animationend` blubbert, das genuegt. Der Puls kann das Ereignis nicht
       * ausloesen: er laeuft endlos und wird beim Fund abgebrochen, was
       * `animationcancel` liefert, nicht `animationend`.
       *
       * Listener am Abort-Signal, Timer in `cleanupTimers`: beides gehoert zu
       * DIESEM Connect und muss beim Abraeumen mitgehen. Heute faellt das nicht
       * auf -- der Timer feuert immer, und `clearLayer()` hat das Element bis
       * dahin ohnehin abgehaengt, das spaete `element.remove()` ist also ein
       * No-op. Die Regel gilt trotzdem, denn Task 13 laesst diese Komponente bei
       * `shopify:section:load` wiederholt ab- und aufbauen, und jede andere
       * Registrierung in dieser Datei haelt sich daran.
       */
      removeObject(entry) {
        const element = entry.element;
        let done = false;
        let timer = null;
        const finish = () => {
          if (done) return;
          done = true;
          window.clearTimeout(timer);
          this.cleanupTimers.delete(timer);
          element.remove();
        };
        // once + signal: der Listener raeumt sich in jedem Fall selbst weg, auch
        // wenn das Sicherheitsnetz zuerst greift.
        element.addEventListener('animationend', finish, {
          once: true,
          signal: this.abortController.signal,
        });
        timer = window.setTimeout(finish, HuntGame.FOUND_CLEANUP_TIMEOUT);
        this.cleanupTimers.add(timer);
      }

      /**
       * Gewinn. Erreichbar aus genau zwei Pfaden: buildRound() (eine Runde, in
       * der nichts mehr zu finden ist) und dem letzten Fund. Der Reflow-Pfad
       * ruft ihn NIE -- siehe parkObject().
       *
       * Der Merker geht sofort in den localStorage, die Karte wechselt erst nach
       * WIN_DELAY: sonst ueberlagert das Popup die Animation des Objekts, das
       * das Spiel gerade abgeschlossen hat.
       *
       * Der Timer laeuft ab HIER, also ab dem Klick -- dieser Aufruf ist
       * synchron in handleObjectClick(). Er wartet NICHT auf `animationend`,
       * und WIN_DELAY ist kuerzer als FOUND_ANIMATION: die Begruendung, warum
       * das gewollt ist, steht an WIN_DELAY.
       */
      handleWin() {
        if (this.winPending) return;
        this.winPending = true;
        this.writeWon();
        this.winTimer = window.setTimeout(() => {
          this.winTimer = null;
          this.clearLayer();
          this.state = 'won';
          this.updateCounter();
          this.openPopup();
        }, HuntGame.WIN_DELAY);
      }

      /**
       * Oeffnet das Gewinn-Popup. Es ist Geschwister dieser Section (siehe
       * sections/hunt-game.liquid) und wird darum ueber seine ID gesucht.
       *
       * `show(null)`: der Basisklasse wird BEWUSST kein Rueckgabe-Element
       * gegeben. Der Gewinn kommt aus einem Timer, es gibt also kein
       * `event.target`, an das BasicModal den Fokus zurueckgeben koennte -- und
       * ihr `removeTrapFocus(activeElement)` fokussiert ohne `preventScroll`,
       * was die gerade wiederhergestellte Scroll-Position erneut verschoebe.
       * Den Rueckweg macht das Popup selbst (siehe restoreFocus dort).
       *
       * Fehlt das Popup (Snippet nicht gerendert, Element noch nicht
       * registriert), passiert nichts: die Gewinn-Karte zeigt Code und Text
       * bereits, das Spiel bleibt vollstaendig bedienbar.
       */
      openPopup() {
        if (!this.settings.popupId) return;
        const popup = document.getElementById(this.settings.popupId);
        if (popup && typeof popup.show === 'function') popup.show(null);
      }

      /**
       * Gegenstueck zu openPopup(), gebraucht von `shopify:section:deselect`
       * und vom Abraeumen einer neu gerenderten Section.
       *
       * `hide()` der Basisklasse ist ein No-op, wenn das Popup nicht offen ist
       * (ModalComponent.hide prueft `this.open`), der Aufruf braucht also keine
       * eigene Zustandsabfrage. Die Suche laeuft wie in openPopup() ueber die
       * ID: das Popup ist Geschwister dieser Section und haengt nach dem ersten
       * Oeffnen an document.body.
       */
      closePopup() {
        if (!this.settings.popupId) return;
        const popup = document.getElementById(this.settings.popupId);
        if (popup && typeof popup.hide === 'function') popup.hide();
      }

      /**
       * Popup-Vorschau des Theme-Editors (`preview_popup`, Design-Spec 8).
       *
       * Nicht direkt openPopup(), sondern ueber whenDefined: in DIESER Datei
       * steht `customElements.define('hunt-game')` VOR defineHuntGamePopup().
       * Beim ersten connectedCallback ist `<hunt-game-popup>` also noch ein
       * nicht upgradetes HTMLElement ohne show() -- openPopup() faellt dann
       * still durch. Fuer den Gewinn-Pfad ist das richtig (der laeuft aus einem
       * Timer, lange nach jeder Registrierung); fuer die Vorschau beim Laden des
       * Editors waere es ein stummer Ausfall, und der Merchant haette einen
       * Schalter, der nichts tut.
       *
       * Kein Listener, also kein `{ signal }` -- die Abbruchbedingung steht
       * darum im Rueckruf: der Editor kann die Section in der Zwischenzeit neu
       * gerendert haben, dann gehoert dieses Element nicht mehr zur Seite.
       */
      openPopupPreview() {
        if (!this.settings.previewPopup) return;
        const signal = this.abortController.signal;
        customElements.whenDefined('hunt-game-popup').then(() => {
          if (signal.aborted || !this.isConnected) return;
          this.openPopup();
        });
      }

      /**
       * Theme-Editor-Integration (Design-Spec 9).
       *
       * `Shopify.designMode` wird ZUSAETZLICH zu data-design-mode geprueft. Das
       * Attribut rendert sections/hunt-game.liquid nur innerhalb
       * `{% if request.design_mode %}`, im Live-Shop fehlt es also samt
       * data-preview-objects und data-preview-popup -- ein vergessener Haken
       * kann keinem Kunden das Popup zeigen. Die zweite Abfrage haengt dieselbe
       * Zusicherung an eine von Liquid unabhaengige Quelle.
       *
       * KEIN `shopify-design-mode`-Attribut am Popup, obwohl ModalComponent
       * (theme.js:695) genau fuer diesen Zweck gebaut ist: BasicModal traegt
       * `requiresBodyAppended`, das Popup haengt nach dem ersten Oeffnen an
       * document.body, und `closest('.shopify-section')` ist dort null.
       * theme.js:697-699 ruft `section.addEventListener` OHNE Null-Check -- der
       * naechste connectedCallback bricht mit einem TypeError ab und nimmt die
       * restliche Initialisierung mit. Gemessen: closest() liefert die Section
       * vor dem Body-Append, null danach. Darum steuert diese Komponente die
       * Vorschau selbst, ueber openPopupPreview() und closePopup().
       */
      setupDesignMode() {
        if (!this.designMode) return;
        if (!window.Shopify || !window.Shopify.designMode) return;
        const section = this.closest('.shopify-section');
        if (!section) return;
        const signal = this.abortController.signal;

        // `shopify:section:unload` feuert nicht nur beim Loeschen der Section,
        // sondern auch, BEVOR der Editor sie neu rendert. Ohne Aufraeumen
        // sammeln sich bei jeder Setting-Aenderung verwaiste Objekte auf der
        // Seite an: die Ebene haengt an document.body und ueberlebt den
        // Austausch der Section.
        //
        // Das Popup gehoert mit abgeraeumt, und zwar aus demselben Grund: ist
        // es offen, steht es ebenfalls an document.body und wird vom Austausch
        // nicht angefasst. Es blieb sonst als zweites, verwaistes Modal mit
        // derselben ID sichtbar stehen, waehrend die neu gerenderte Section ihr
        // eigenes mitbringt. hide() schiebt es beim Schliessen selbst aus dem
        // body zurueck.
        section.addEventListener(
          'shopify:section:unload',
          () => {
            this.clearLayer();
            this.closePopup();
          },
          { signal }
        );

        // Neuaufbau nach einem Re-Render. Bewusst NUR buildRound() und keine
        // eigene Initialisierungsroutine.
        //
        // Im Regelfall ersetzt der Editor die Section samt <hunt-game>, und
        // connectedCallback laeuft von selbst erneut -- der vollstaendige Weg
        // inklusive readSettings(). Dieser Listener deckt den Fall ab, in dem
        // das Ereignis ein Element erreicht, dessen Runde noch laeuft: dann
        // werden nur die Objekte neu gewuerfelt. buildRound() raeumt die Ebene
        // selbst ab (clearLayer() vor dem Messgatter) und ruft am Ende
        // observeLayout(), das seinerseits idempotent ist -- es steht danach
        // genau eine Ebene.
        //
        // ZWEI FILTER, und der erste ist der wichtigere.
        //
        // 1. roundBuiltOnConnect: der erste `load` nach einem Connect ist
        //    bereits erledigt. Gemessen (Review-Runde 1) an der echten Folge
        //    des Editors bei EINER Setting-Aenderung:
        //
        //      shopify:section:unload        -> builds 0, Ebenen 0, Objekte 0
        //      neues <hunt-game> verbunden   -> builds 1, playing, 1 Ebene, 6 Objekte
        //      shopify:section:load          -> builds 2  (einer zu viel)
        //
        //    Das Endergebnis war korrekt (eine Ebene, sechs Objekte, Invariante
        //    intakt), aber der Merchant sah die Objekte bei jeder Aenderung
        //    zweimal neu wuerfeln und zahlte zwei vollstaendige Erhebungen von
        //    Bild-, Flaechen- und Sperrkandidaten ueber den ganzen Suchbereich.
        //
        //    Ein Zustandsfilter allein kann das NICHT leisten: restoreState()
        //    hat das frisch verbundene Element mit preview_objects schon auf
        //    `playing` gebracht, bevor das Ereignis der Plattform eintrifft --
        //    es steht damit in genau demselben Zustand wie eine Runde, die ein
        //    Verschieben der Section ueberlebt hat, und die beiden sind von
        //    aussen nicht unterscheidbar. Der Merker sitzt am ELEMENT und gilt
        //    darum in beiden Faellen: ob der Editor den
        //    .shopify-section-Wrapper behaelt oder ersetzt, das Ereignis
        //    erreicht so oder so ein Element, das schon gebaut hat.
        //
        // 2. Der Zustandsfilter bleibt: in `idle` liegt nichts, was neu zu
        //    bauen waere, und in `won` wuerde buildRound() eine Runde starten,
        //    die niemand angefordert hat.
        section.addEventListener(
          'shopify:section:load',
          () => {
            if (this.roundBuiltOnConnect) {
              this.roundBuiltOnConnect = false;
              return;
            }
            if (this.state === 'playing') this.buildRound();
          },
          { signal }
        );

        // Auswahl der Section im Editor. openPopupPreview() traegt die
        // preview_popup-Abfrage selbst.
        section.addEventListener('shopify:section:select', () => this.openPopupPreview(), {
          signal,
        });

        // Andere Section angeklickt. Bedingungslos schliessen: ein Popup, das
        // der Merchant ueber das X geschlossen hat und ein Popup, das nie offen
        // war, sind fuer hide() derselbe No-op.
        section.addEventListener('shopify:section:deselect', () => this.closePopup(), { signal });
      }
    }
  );
}

/**
 * Das Gewinn-Popup. Erbt direkt von `BasicModal` (theme.js) -- Overlay,
 * Escape, Focus-Trap und der Klick auf `[aria-controls]` kommen von dort.
 * Bewusst NICHT von `assets/subscription-popup.js`: das haengt an Newsletter,
 * Cookie-Namen und `data-trigger-open` (Design-Spec 4.3).
 *
 * In einer Funktion, weil die Registrierung zweimal versucht werden muss --
 * siehe den Aufruf darunter.
 *
 * @returns {boolean} false, wenn `BasicModal` noch nicht geladen ist.
 */
function defineHuntGamePopup() {
  // Kein `window.BasicModal`: `class BasicModal` ist in theme.js eine
  // top-level-Deklaration und damit eine globale LEXIKALISCHE Bindung, kein
  // window-Property. `typeof` ist die einzige Pruefung, die auf eine nicht
  // vorhandene Bindung nicht mit einem ReferenceError antwortet.
  if (typeof BasicModal === 'undefined') return false;
  if (customElements.get('hunt-game-popup')) return true;

  customElements.define(
    'hunt-game-popup',
    class HuntGamePopup extends BasicModal {
      connectedCallback() {
        super.connectedCallback();
        // Die zweite Instanz der Code-Box. connectedCallback laeuft bei jedem
        // Oeffnen erneut, weil BasicModal (requiresBodyAppended) das Element an
        // document.body haengt und beim Schliessen zurueckschiebt -- der
        // Listener haengt am dann NEUEN AbortController und geht mit ihm.
        window.HuntGameClipboard.attach(this, this.abortController.signal);
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        // HIER WIRD DIE FAHRT AUSDRUECKLICH NICHT ABGEBROCHEN.
        //
        // Ein cancelCardScroll() an dieser Stelle hat die Fahrt zuverlaessig
        // getoetet: BasicModal haengt das Popup zum Oeffnen an document.body
        // und schiebt es beim Schliessen zurueck. Dieser Umzug loest
        // disconnectedCallback aus -- unmittelbar nachdem handleAfterHide die
        // Fahrt gestartet hat. Nachgemessen mit dem echten Umzug: Frame
        // gesetzt, nach dem Umzug abgebrochen, Endposition gleich der
        // Startposition. Es bewegte sich nichts, auch in Chrome am Desktop.
        //
        // Ein Abbruch ist hier auch nicht noetig: die Fahrt haelt Start und
        // Ziel als ZAHLEN und ruft nur window.scrollTo. Sie fasst das
        // Popup-Element nie an und kann durch seinen Umzug nichts brechen.
        // Ihre Listener raeumt sie am Ende selbst ab.
        // Wird die Section im Theme-Editor bei offenem Popup neu gerendert,
        // bliebe der Body sonst fuer immer gesperrt. Das Umhaengen an
        // document.body loest diesen Callback ebenfalls aus, dort ist aber noch
        // nicht gesperrt (das passiert erst in handleAfterShow) -- unlockScroll
        // ist in diesem Fall ein No-op.
        this.unlockScroll();
      }

      /** Die zugehoerige Section. Ueber data-popup-id, weil das Popup beim
       * Oeffnen aus seinem Elternknoten heraus an document.body wandert. */
      get huntGameHost() {
        if (this._host && this._host.isConnected) return this._host;
        // Attributvergleich statt Selektor-String: dann muss die ID nicht
        // maskiert werden.
        this._host =
          Array.prototype.find.call(
            document.querySelectorAll('hunt-game'),
            (element) => element.dataset.popupId === this.id
          ) || null;
        return this._host;
      }

      handleAfterShow() {
        super.handleAfterShow();
        this.lockScroll();
      }

      handleAfterHide() {
        super.handleAfterHide();
        // Reihenfolge: entsperren, zur Karte springen, dann fokussieren.
        //
        // unlockScroll() MUSS zuerst laufen und setzt die Seite auf die
        // Position zurueck, an der das Popup aufging. Ein Sprung davor waere
        // davon sofort ueberschrieben.
        //
        // restoreFocus() zuletzt, und es behaelt preventScroll: true -- das
        // Scrollen hat scrollToCard() schon erledigt, und ein zweiter,
        // ungesteuerter Sprung durch focus() waere hier nur Unruhe.
        this.unlockScroll();
        this.scrollToCard();
        this.restoreFocus();
      }

      /**
       * Zielposition der Karte in Dokumentkoordinaten: mittig im Bildschirm,
       * geklemmt auf den gueltigen Scrollbereich.
       *
       * Mittig und nicht oben, weil dieses Theme einen Sticky-Header hat
       * (z-index 21, 56px) -- oben angesetzt laege die Kartenoberkante
       * darunter.
       */
      cardScrollTarget(card) {
        const rect = card.getBoundingClientRect();
        const mitte = rect.top + window.scrollY + rect.height / 2;
        const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        return Math.max(0, Math.min(max, Math.round(mitte - window.innerHeight / 2)));
      }

      /** Laufende Fahrt abbrechen und ihre Listener abraeumen. */
      cancelCardScroll() {
        if (this.cardScrollFrame) {
          window.cancelAnimationFrame(this.cardScrollFrame);
          this.cardScrollFrame = null;
        }
        if (this.cardScrollAbort) {
          this.cardScrollAbort.abort();
          this.cardScrollAbort = null;
        }
      }

      /**
       * Zurueck zur Spielkarte, als weiche Fahrt -- aber SELBST gefahren.
       *
       * WOZU DER SPRUNG. Wer alle Objekte findet, hat sich dafuer bis zum
       * Seitenende gescrollt; das Popup geht also fast immer weit unten auf.
       * Ohne ihn bleibt der Kunde nach dem Schliessen im Seitenfuss stehen,
       * waehrend die Karte mit dem Rabattcode weit oben liegt. Er behebt
       * ausserdem, dass restoreFocus() den Fokus auf den Neustart-Link setzt,
       * ohne zu scrollen -- das fokussierte Element lag sonst ausserhalb des
       * Bildschirms.
       *
       * WARUM EINE EIGENE FAHRT UND NICHT behavior 'smooth'. Genau daran ist
       * die erste Fassung auf iOS gescheitert: die Seite sprang sichtbar erst
       * an den Seitenanfang und dann zur Karte (Meldung des Product Owners).
       * Eine weiche Browser-Animation ist asynchron und nimmt als Startpunkt,
       * was der Compositor schon uebernommen hat. Unmittelbar davor hebt
       * unlockScroll body{position:fixed} auf und setzt die Position per
       * scrollTo zurueck -- in Chromium sofort wirksam (nachgemessen), auf iOS
       * offenbar nicht. Dort begann die Animation bei 0.
       *
       * Eine eigene Fahrt hat dieses Problem nicht: der Startwert wird HIER
       * gelesen und jeder Frame setzt eine ABSOLUTE Position. Der erste Frame
       * setzt dabei ausdruecklich den Startwert, noch bevor der Browser
       * zeichnet -- ein Umweg ueber den Seitenanfang ist damit konstruktiv
       * ausgeschlossen, egal was der Compositor gerade meint.
       *
       * Sie beginnt im naechsten Frame, also ohne Verzoegerung.
       *
       * ABBRUCH BEI BERUEHRUNG. Faengt der Kunde selbst an zu wischen, wird
       * abgebrochen. Sonst kaempft die Fahrt gegen seine Hand -- auf einem
       * Telefon der unangenehmste denkbare Zustand.
       *
       * Bei prefers-reduced-motion wird hart gesprungen. Eine Fahrt ueber
       * mehrere Bildschirmhoehen ist genau die Bewegung, die diese Einstellung
       * abbestellt.
       */
      scrollToCard() {
        const host = this.huntGameHost;
        if (!host || !host.settings || !host.settings.scrollToCard) return;
        const card = host.querySelector('.hunt-game__card') || host;
        const start = window.scrollY;
        const ziel = this.cardScrollTarget(card);
        // Unter zwei Pixeln ist nichts zu fahren.
        if (Math.abs(ziel - start) < 2) return;

        const reduced =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduced) {
          window.scrollTo(0, ziel);
          return;
        }

        this.cancelCardScroll();

        // Dauer aus der Strecke, aber gedeckelt: eine kurze Strecke soll nicht
        // kriechen, eine lange nicht ewig dauern.
        const strecke = Math.abs(ziel - start);
        const dauer = Math.min(600, Math.max(260, Math.round(strecke * 0.35)));

        this.cardScrollAbort = new AbortController();
        const abbrechen = () => this.cancelCardScroll();
        for (const ereignis of ['touchstart', 'wheel', 'pointerdown', 'keydown']) {
          window.addEventListener(ereignis, abbrechen, {
            passive: true,
            signal: this.cardScrollAbort.signal,
          });
        }

        let t0 = null;
        const schritt = (jetzt) => {
          if (t0 === null) t0 = jetzt;
          const anteil = dauer === 0 ? 1 : Math.min(1, (jetzt - t0) / dauer);
          // Ease-out (kubisch): schnell los, sanft ankommen.
          const weich = 1 - Math.pow(1 - anteil, 3);
          window.scrollTo(0, Math.round(start + (ziel - start) * weich));
          if (anteil < 1) {
            this.cardScrollFrame = window.requestAnimationFrame(schritt);
          } else {
            this.cardScrollFrame = null;
            this.cancelCardScroll();
          }
        };
        this.cardScrollFrame = window.requestAnimationFrame(schritt);
      }

      /**
       * theme.css:1603 sperrt mit `overflow: hidden` am Body. Das wirkt auf iOS
       * Safari NICHT; die Seite scrollt hinter dem offenen Modal weiter -- was
       * das Newsletter-Popup dieses Themes heute ebenso trifft.
       *
       * Behoben wird das nur HIER, in dieser Komponente: die globale Theme-CSS
       * anzutasten haette Cart-Drawer, Menue und Newsletter-Popup mitveraendert,
       * ohne fuer dieses Feature etwas zu gewinnen (Design-Spec 10.9). Ein
       * globaler Fix gehoert in ein eigenes Ticket.
       */
      lockScroll() {
        if (this.scrollLocked) return;
        this.scrollLocked = true;
        this.savedScrollY = window.scrollY;
        const root = document.documentElement;
        /*
         * Die Dokumenthoehe FESTHALTEN, bevor der Body fixiert wird.
         *
         * Ohne das faellt die Hoehe des Dokuments mit body{position:fixed} auf
         * die Bildschirmhoehe zusammen -- nachgemessen: 2573px werden zu 844px
         * bei 844px Bildschirm. Die Seite ist damit nicht mehr scrollbar, und
         * genau darauf reagiert iOS Safari: es fahrt die eingeklappte
         * Adressleiste und die Werkzeugleiste wieder aus. Das verkleinert den
         * sichtbaren Bereich, loest einen Umbruch aus, und Seite samt Popup
         * "blitzen" ein zweites Mal auf (Meldung des Product Owners, seine
         * Erklaerung war zutreffend).
         *
         * Mit gehaltener Hoehe bleibt das Dokument scrollbar, die Leisten
         * bleiben eingeklappt. Die SPERRE leidet nicht darunter: der Body ist
         * weiter fixiert, ein Scrollversuch verschiebt den Inhalt also nicht.
         * Nachgemessen mit einem Sprung auf 2200px bei gesperrter Seite --
         * Inhalt um 0px verschoben, mit und ohne gehaltene Hoehe.
         *
         * Ob es auf iOS wirklich die Leisten eingeklappt laesst, ist NICHT
         * geprueft; dafuer braucht es ein Geraet. Belegt ist nur, dass die
         * Seite scrollbar bleibt und die Sperre haelt.
         */
        this.savedRootHeight = root.style.height;
        root.style.height = `${root.scrollHeight}px`;
        const body = document.body;
        body.style.position = 'fixed';
        body.style.top = `${-this.savedScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
      }

      unlockScroll() {
        if (!this.scrollLocked) return;
        this.scrollLocked = false;
        const body = document.body;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        // Der fixierte Body hat die Seite auf 0 gesetzt; ohne dieses
        // Zuruecksetzen landet der Kunde nach dem Schliessen am Seitenanfang.
        if (typeof this.savedScrollY === 'number') window.scrollTo(0, this.savedScrollY);
        this.savedScrollY = null;
        // Die festgehaltene Dokumenthoehe ZULETZT loesen, nach dem
        // Ruecksprung. Andernfalls faellt das Dokument fuer einen Moment auf
        // seine natuerliche Hoehe zurueck, waehrend der Body noch fixiert war,
        // und der Ruecksprung wuerde am kleineren Scrollbereich abgeschnitten.
        // Der Wert entspricht der natuerlichen Hoehe, das Loesen aendert die
        // Darstellung also nicht.
        if (typeof this.savedRootHeight === 'string') {
          document.documentElement.style.height = this.savedRootHeight;
          this.savedRootHeight = null;
        }
      }

      /**
       * Fokus-Rueckweg. Zwei Fallen:
       * 1. Der Gewinn kommt aus einem Timer -- es gibt kein ausloesendes
       *    Element, an das BasicModal den Fokus zurueckgeben koennte.
       * 2. `[data-hunt-game-restart]` existiert nur, wenn `restart_label`
       *    gesetzt ist; die Redaktion kann das Feld leeren. Ohne Guard waere
       *    der Rueckweg dann ein `null.focus()`.
       *
       * Der Start-Button ist KEIN Kandidat: er liegt in der idle-Ebene, die im
       * Zustand `won` `visibility: hidden` traegt und damit nicht fokussierbar
       * ist. Bleibt der Host -- der braucht ein `tabindex`, sonst nimmt er
       * keinen programmatischen Fokus an.
       *
       * `preventScroll`: der Fokus darf die eben wiederhergestellte
       * Scroll-Position nicht erneut verschieben.
       */
      restoreFocus() {
        const host = this.huntGameHost;
        if (!host) return;
        const target = host.querySelector('[data-hunt-game-restart]') || host;
        if (target === host && !host.hasAttribute('tabindex')) host.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    }
  );
  return true;
}

// Zweiter Versuch nach DOMContentLoaded: laeuft diese Datei vor theme.js, ist
// `BasicModal` noch nicht deklariert und `customElements.define` fiele STUMM
// aus -- das Popup wuerde sich nie oeffnen. Deferred Scripts laufen in
// Dokumentreihenfolge und vor DOMContentLoaded, der zweite Versuch findet die
// Klasse also in jedem Fall. Wird die Section spaeter nachgeladen
// (shopify:section:load), ist theme.js ohnehin durch und der erste Versuch
// greift.
if (!defineHuntGamePopup()) {
  document.addEventListener('DOMContentLoaded', defineHuntGamePopup, { once: true });
}
